import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PetContextService } from 'src/app/services/pet-context.service';
import { PetAlertKind, petBirthdayNearby } from 'src/app/services/pet-context.models';
import { GetAllQuery, PatientApi, PatientListDto } from 'src/app/services/api-service/patient/patient-api';
import { MutiTreatmentListDto } from 'src/app/services/api-service/register/mutiTreatmentListDto';
import { RegisterClinicHistoryDTO, RegisterPeriodItemDTO } from 'src/app/services/api-service-dentist/register/register-api.model';
import { RegisterApiService } from 'src/app/services/api-service-dentist/register/register-api.service';
import { HistApi } from 'src/app/services/api-service/hist/hist-api';
import { WholeHist } from 'src/app/services/api-service/hist/whole-hist';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { HandleErrorService } from 'src/app/services/handle-error.service';
import { PatientInfoPetBirthdayReference, PatientInfoWindowService } from 'src/app/services/patient-info-window.service';
import { RegisterService } from 'src/app/services/register-service';
import { ReserveItem, ReserveService } from 'src/app/services/reserve.service';
import { RouteInfoService } from 'src/app/services/route-info.service';
import { UserConfirmService } from 'src/app/services/user-confirm.service';
import { UserCache } from 'src/app/services/user-cache';
import { ClinicDataService } from 'src/app/services/data-service/clinic-data-service';
import { RegisterConst } from 'src/app/shared/constants/register-const';
import { DateHelper } from 'src/app/shared/helpers/date-helper';
import { HistOrder } from 'src/app/hist/models/hist-order';
import { OrderTypeEnum } from 'src/app/enums/OrderTypeEnum';

export interface PatientInfoPinnedLayout {
  side: 'top' | 'right' | 'bottom' | 'left';
  size: number;
}

interface PatientInfoTextOption {
  text: string;
  value: any;
}

interface PatientInfoAlertTag {
  label: string;
  tooltip: string;
  cssClass: string;
}

interface PatientInfoSimpleHistState {
  visible: boolean;
  loading: boolean;
  error: string;
  regId: number;
  source: RegisterClinicHistoryDTO | null;
  data: WholeHist | null;
  maskVisible: boolean;
  moved: boolean;
  dragging: boolean;
  resizing: boolean;
  resizeDir: string;
  resizeHoverDir: string;
  startX: number;
  startY: number;
  startOffsetX: number;
  startOffsetY: number;
  startRect: { left: number, top: number, width: number, height: number };
  rect: { left: number, top: number, width: number, height: number };
  pinned: boolean;
  pinSide: '' | 'top' | 'right' | 'bottom' | 'left';
  pinnedSize: number;
  floatingRectBeforePin: { left: number, top: number, width: number, height: number } | null;
}

type PatientInfoSimpleHistRect = { left: number, top: number, width: number, height: number };

interface PatientInfoSimpleHistMemoryLayout {
  rect: PatientInfoSimpleHistRect;
  pinned: boolean;
  pinSide: '' | 'top' | 'right' | 'bottom' | 'left';
  pinnedSize: number;
  floatingRectBeforePin: PatientInfoSimpleHistRect | null;
}

type PatientInfoSectionKey = 'family' | 'activeTreatments' | 'periodItems' | 'reserveRecords' | 'clinicHistories';
type PatientInfoSelectionSource = 'default' | 'family' | 'preserve';

@Component({
  selector: 'app-patient-info-window',
  templateUrl: './patient-info-window.component.html',
  styleUrls: ['./patient-info-window.component.css']
})
export class PatientInfoWindowComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('patientInfoDentalTreatmentEndTpl')
  patientInfoDentalTreatmentEndTpl: TemplateRef<any>;

  @Input() selectedPatient: PatientListDto = null;
  @Input() selectedRegisterItem: any = null;
  @Input() patientId = 0;
  @Input() enableEdit = true;
  @Input() memoryScope = 'global';
  @Input() refreshToken = 0;
  @Input() refreshPatientId = 0;

  @Output() selectedPatientChange = new EventEmitter<PatientListDto>();
  @Output() editPatient = new EventEmitter<{ patient: PatientListDto, registerItem?: any }>();
  @Output() reservePatient = new EventEmitter<{ patient: PatientListDto, registerItem?: any }>();
  @Output() dentalFollow = new EventEmitter<{ patient: PatientListDto, treatment: MutiTreatmentListDto }>();
  @Output() layoutChange = new EventEmitter<PatientInfoPinnedLayout | null>();
  @Output() resizeStateChange = new EventEmitter<boolean>();

  readonly patientInfoFloatingResizeHandles = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
  readonly patientInfoMinWidth = 305;
  readonly patientInfoMinHeight = 180;
  readonly patientInfoMinimizedSize = 56;
  readonly patientInfoPinnedMinimizedThickness = 10;
  readonly patientInfoPinDetachDistance = 24;
  readonly patientInfoSimpleHistMinWidth = 360;
  readonly patientInfoSimpleHistMinHeight = 240;
  readonly patientInfoSimpleHistDefaultWidth = 860;
  readonly patientInfoSimpleHistDefaultHeight = 600;
  readonly patientInfoSimpleHistDragThreshold = 3;

  patientInfoWindow = {
    rect: { left: 32, top: 136, width: 360, height: 320 },
    minimizedRect: { left: 32, top: 136, width: 56, height: 56 },
    startRect: { left: 0, top: 0, width: 0, height: 0 },
    floatingRectBeforePin: null as { left: number, top: number, width: number, height: number },
    restoreState: null as {
      pinned: boolean,
      pinSide: '' | 'top' | 'right' | 'bottom' | 'left',
      pinnedSize: number,
      rect: { left: number, top: number, width: number, height: number }
    },
    pinned: false,
    pinSide: '' as '' | 'top' | 'right' | 'bottom' | 'left',
    pinnedSize: 360,
    minimized: false,
    dragging: false,
    resizing: false,
    resizeDir: '',
    resizeHoverDir: '',
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    startPinnedSize: 0,
    startPinSide: '' as '' | 'top' | 'right' | 'bottom' | 'left',
    moved: false
  };

  patientInfoSearchKeyword = '';
  patientInfoSearchKeywordHistory: string[] = [];
  patientInfoSearchSuggestions: string[] = [];
  patientInfoSearchSuggestionsVisible = false;
  patientInfoSearchResults: PatientListDto[] = [];
  patientInfoSearchResultsKeyword = '';
  patientInfoSearchResultsCollapsed = true;
  patientInfoSelectedPatient: PatientListDto = null;
  patientInfoSearchLoading = false;
  patientInfoSearchError = '';
  patientInfoFamilyMembers: PatientListDto[] = [];
  patientInfoFamilyLoading = false;
  patientInfoActiveTreatments: MutiTreatmentListDto[] = [];
  patientInfoPeriodItems: RegisterPeriodItemDTO[] = [];
  patientInfoReserveRecords: ReserveItem[] = [];
  patientInfoClinicHistories: RegisterClinicHistoryDTO[] = [];
  patientInfoRegisterInfoLoading = false;
  patientInfoClinicHistoryPage = 1;
  patientInfoCurrentAgeClass = '';
  patientInfoAlertTags: PatientInfoAlertTag[] = [];
  patientInfoSimpleHist: PatientInfoSimpleHistState = {
    visible: false,
    loading: false,
    error: '',
    regId: 0,
    source: null,
    data: null,
    maskVisible: false,
    moved: false,
    dragging: false,
    resizing: false,
    resizeDir: '',
    resizeHoverDir: '',
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    startRect: { left: 0, top: 0, width: 0, height: 0 },
    rect: { left: 0, top: 0, width: 0, height: 0 },
    pinned: false,
    pinSide: '',
    pinnedSize: 0,
    floatingRectBeforePin: null
  };
  patientInfoSectionExpanded = {
    family: true,
    activeTreatments: true,
    periodItems: true,
    reserveRecords: true,
    clinicHistories: true
  };

  private readonly patientInfoSearchKeywordHistoryLimit = 100;
  private readonly patientInfoSearchSuggestionLimit = 10;
  private readonly patientInfoClinicHistoryPageSize = 5;
  private readonly patientInfoMemoryKeyPrefix = 'patient-info-window';
  private readonly legacyMemoryKeyPrefix = 'waiting-list-dentistry-patient-info';
  private readonly patientInfoDefaultSpcIdentityOptions: PatientInfoTextOption[] = [
    { value: 0, text: '原住民' },
    { value: 1, text: '小兒麻痺' },
    { value: 2, text: '中低收入戶' },
    { value: 3, text: '低收入戶' },
    { value: 4, text: '平地原住民族地區' },
    { value: 5, text: '山地原住民族地區' },
    { value: 6, text: '身心障礙' },
    { value: 7, text: '偏遠地區' },
    { value: 8, text: '離島地區' }
  ];
  private patientInfoDetailIdentity = '';
  private patientInfoDetailRequestId = 0;
  private patientInfoFamilyRequestId = 0;
  private patientInfoRegisterInfoRequestId = 0;
  private patientInfoSimpleHistRequestId = 0;
  private patientInfoSimpleHistLayouts: { [routeKey: string]: PatientInfoSimpleHistMemoryLayout } = {};
  private patientInfoSpecialIdentityOptions: PatientInfoTextOption[] = [];
  private patientInfoSpecialIdentityOptionsLoading = false;
  private patientInfoSpecialIdentityOptionsLoaded = false;
  private patientInfoRoomOptions: PatientInfoTextOption[] = [];
  private patientInfoRoomOptionsLoaded = false;
  private patientInfoRouteSubscription: Subscription;
  private patientInfoPetBirthdaySubscription: Subscription;
  private patientInfoPetBirthdayReference: PatientInfoPetBirthdayReference = null;
  private lastPatientInfoRegReserveRoute = false;
  private isDestroyed = false;

  constructor(
    private petContext: PetContextService,
    private patientApi: PatientApi,
    private histApi: HistApi,
    private registerApi: RegisterApiService,
    private registerService: RegisterService,
    private reserveService: ReserveService,
    private patientInfoWindowService: PatientInfoWindowService,
    private clinicDataService: ClinicDataService,
    private notification: EasyNotificationService,
    private handleErrorService: HandleErrorService,
    private userConfirmService: UserConfirmService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private routeInfo: RouteInfoService
  ) {
  }

  ngOnInit() {
    this.patientInfoPetBirthdaySubscription = this.patientInfoWindowService.petBirthdayReference$.subscribe(reference => {
      this.patientInfoPetBirthdayReference = reference;
      this.publishPetContext();
    });
    const hasWindowState = this.loadPatientInfoMemory();
    const hasRoutePinnedLayout = this.applyRoutePinnedLayout();
    if (!hasWindowState && !hasRoutePinnedLayout) {
      this.pinPatientInfoWindowToSide('left', this.patientInfoWindow.rect);
    }
    this.syncSelectedPatientInput();
    this.syncRegisterItemInput(false);
    this.loadPatientFromIdIfNeeded();
    this.loadPatientInfoSelectedDetail();
    void this.loadPatientInfoRoomOptions();
    this.bindPatientInfoRoutePolicy();
    this.applyPatientInfoRouteSectionPolicy(true);
    this.emitLayoutChange();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedPatient && !changes.selectedPatient.firstChange) {
      this.syncSelectedPatientInput();
    }

    if (changes.selectedRegisterItem && !changes.selectedRegisterItem.firstChange) {
      this.syncRegisterItemInput(false);
      this.publishPetContext();
    }

    if (changes.patientId && !changes.patientId.firstChange) {
      this.loadPatientFromIdIfNeeded();
    }

    if (changes.refreshToken && !changes.refreshToken.firstChange) {
      void this.refreshPatientInfoContent();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.petContext.release('patient-info-window');
    if (this.patientInfoWindow.resizing) {
      this.resizeStateChange.emit(false);
    }
    this.removePatientInfoSimpleHistDocumentListeners();
    this.patientInfoRouteSubscription?.unsubscribe();
    this.patientInfoPetBirthdaySubscription?.unsubscribe();
    this.removePatientInfoDocumentListeners();
  }

  get patientInfoWindowTitle(): string {
    return this.getPatientInfoDisplayName(this.patientInfoSelectedPatient)
      || this.selectedRegisterItem?.PatientName
      || '未選取患者';
  }

  get patientInfoMinimizedLabel(): string {
    const name = this.patientInfoWindowTitle;
    return name ? name.substr(0, 1) : '患';
  }

  get patientInfoPinnedMinimizedIcon(): string {
    switch (this.patientInfoWindow.pinSide) {
      case 'left':
        return 'chevron_right';
      case 'right':
        return 'chevron_left';
      case 'top':
        return 'expand_more';
      case 'bottom':
        return 'expand_less';
      default:
        return 'open_in_full';
    }
  }

  get patientInfoResizeHandles(): string[] {
    if (this.patientInfoWindow.minimized) {
      return [];
    }

    if (!this.patientInfoWindow.pinned) {
      return this.patientInfoFloatingResizeHandles;
    }

    switch (this.patientInfoWindow.pinSide) {
      case 'left':
        return ['e'];
      case 'right':
        return ['w'];
      case 'top':
        return ['s'];
      case 'bottom':
        return ['n'];
      default:
        return [];
    }
  }

  get hasPatientInfoPanelContent(): boolean {
    return !!this.patientInfoSelectedPatient
      || !!this.patientInfoSearchError;
  }

  get patientInfoClinicHistoryPageItems(): RegisterClinicHistoryDTO[] {
    const start = (this.patientInfoClinicHistoryPage - 1) * this.patientInfoClinicHistoryPageSize;
    return (this.patientInfoClinicHistories || []).slice(start, start + this.patientInfoClinicHistoryPageSize);
  }

  get patientInfoClinicHistoryTotalPages(): number {
    const total = Math.ceil((this.patientInfoClinicHistories?.length || 0) / this.patientInfoClinicHistoryPageSize);
    return Math.max(total, 1);
  }

  get patientInfoClinicHistoryPageText(): string {
    return `${this.patientInfoClinicHistoryPage}/${this.patientInfoClinicHistoryTotalPages}`;
  }

  get hasPatientInfoClinicHistoryPager(): boolean {
    return (this.patientInfoClinicHistories?.length || 0) > this.patientInfoClinicHistoryPageSize;
  }

  get hasPatientInfoRegisterInfo(): boolean {
    return this.patientInfoRegisterInfoLoading
      || this.patientInfoActiveTreatments.length > 0
      || this.patientInfoPeriodItems.length > 0
      || this.patientInfoReserveRecords.length > 0
      || this.patientInfoClinicHistories.length > 0;
  }

  getPatientInfoTreatmentTypeName(item?: MutiTreatmentListDto): string {
    switch (item?.CusTP) {
      case RegisterConst.SameTreat_5_牙科同療:
        return '牙科療程';
      case RegisterConst.SameTreat_6_牙統療程:
        return '牙科療程';
      default:
        return item?.CusTP ? `療程 ${item.CusTP}` : '療程';
    }
  }

  getPatientInfoTreatmentStartDate(item?: MutiTreatmentListDto): string {
    return this.formatPatientInfoMinguoDate(item?.StartDate);
  }

  getPatientInfoTreatmentCardNo(item?: MutiTreatmentListDto): string {
    return this.formatPatientInfoTreatmentIsqno(item?.Isqno, item?.IsqnoExt);
  }

  getPatientInfoTreatmentCountText(item?: MutiTreatmentListDto): string {
    const cusNo = (item as any)?.CusNo || '';
    const cusTot = item?.CusTot || '';
    if (cusNo && cusTot) {
      return `${cusNo}/${cusTot}`;
    }

    return cusNo || cusTot;
  }

  getPatientInfoTreatmentToothPos(item?: MutiTreatmentListDto): string {
    const positions = this.getPatientInfoTreatmentHRxs(item)
      .map(hrx => hrx?.ToothPos || '')
      .reduce((acc: string[], value: string) => {
        value.toString().split(',').forEach(pos => {
          const trimmed = pos.trim();
          if (trimmed) {
            acc.push(trimmed);
          }
        });
        return acc;
      }, []);
    return Array.from(new Set(positions)).join(', ');
  }

  getPatientInfoTreatmentOrdersDisplay(item?: MutiTreatmentListDto): string {
    const stdCodes = this.getPatientInfoTreatmentHRxs(item)
      .map(hrx => hrx?.StdCode || hrx?.Rx?.StdCode || '')
      .filter(code => !!code);
    return Array.from(new Set(stdCodes)).join(', ');
  }

  getPatientInfoDentalTreatmentHistories(item?: MutiTreatmentListDto): any[] {
    return (item?.HistList || [])
      .slice()
      .sort((a: any, b: any) => this.getPatientInfoDateTime(a?.Register?.RegDate) - this.getPatientInfoDateTime(b?.Register?.RegDate));
  }

  getPatientInfoDentalTreatmentSummaryHistory(item?: MutiTreatmentListDto): any {
    return this.getPatientInfoDentalTreatmentHistories(item)[0] || null;
  }

  getPatientInfoDentalHistoryDate(hist: any): string {
    return this.formatPatientInfoMinguoDate(hist?.Register?.RegDate);
  }

  getPatientInfoDentalHistoryCaseType(hist: any): string {
    return hist?.Hist?.Case || '-';
  }

  getPatientInfoDentalTreatmentCaseType(item?: MutiTreatmentListDto): string {
    return this.getPatientInfoDentalHistoryCaseType(this.getPatientInfoDentalTreatmentSummaryHistory(item));
  }

  getPatientInfoDentalHistoryIdentity(hist: any): string {
    return hist?.Register?.ICode || '-';
  }

  getPatientInfoDentalHistoryOrders(hist: any): string {
    const codes = (hist?.Hist?.HRxs || [])
      .map((hrx: any) => hrx?.StdCode || hrx?.RxCode || hrx?.Rx?.StdCode || hrx?.Rx?.Code || '')
      .filter((code: string) => !!code);
    return Array.from(new Set(codes)).join(', ');
  }

  getPatientInfoDentalHistoryToothPos(hist: any): string {
    const positions = (hist?.Hist?.HRxs || [])
      .map((hrx: any) => hrx?.ToothPos || '')
      .reduce((acc: string[], value: string) => {
        value.toString().split(',').forEach(pos => {
          const trimmed = pos?.trim();
          if (trimmed) {
            acc.push(trimmed);
          }
        });
        return acc;
      }, []);
    const unique = Array.from(new Set(positions));
    return unique.length ? unique.join(', ') : '-';
  }

  getPatientInfoDentalHistoryDateEnd(hist: any): string {
    return this.formatPatientInfoMinguoDate(hist?.Hist?.DateEnd);
  }

  getPatientInfoDentalHistoryIsqnoDisplay(hist: any, item?: MutiTreatmentListDto): string {
    return this.formatPatientInfoTreatmentIsqno(
      hist?.Register?.Icno || hist?.Register?.Isqno || item?.Isqno,
      hist?.Register?.IsqnoExt || item?.IsqnoExt
    ) || '-';
  }

  getPatientInfoDentalTreatmentStatusText(item?: MutiTreatmentListDto): string {
    return this.isPatientInfoDentalTreatmentEnded(item) ? '已結束' : '未完療程';
  }

  getPatientInfoDentalTreatmentDateEnd(item?: MutiTreatmentListDto): string {
    return this.formatPatientInfoMinguoDate(this.getPatientInfoDentalTreatmentDateEndValue(item));
  }

  getPatientInfoDentalTreatmentStartRegId(item?: MutiTreatmentListDto): number {
    return Number(this.getPatientInfoDentalTreatmentSummaryHistory(item)?.Register?.Id || 0);
  }

  isPatientInfoDentalTreatmentEnded(item?: MutiTreatmentListDto): boolean {
    return item?.IsTheEnd === true || !!this.getPatientInfoDentalTreatmentDateEndValue(item);
  }

  onPatientInfoFollowDentalTreatment(item: MutiTreatmentListDto, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item || !this.patientInfoSelectedPatient) {
      return;
    }

    this.dentalFollow.emit({
      patient: this.patientInfoSelectedPatient,
      treatment: item
    });
  }

  async onPatientInfoFinishDentalTreatment(item: MutiTreatmentListDto, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item || !this.patientInfoSelectedPatient) {
      return;
    }

    const dateEnd = this.getPatientInfoDentalTreatmentLastVisitDate(item);
    if (!dateEnd) {
      this.notification.showError('找不到療程最後一筆看診日，無法結束療程');
      return;
    }

    const confirmed = await this.userConfirmService.showConfirm({
      title: '結束療程',
      msg: '是否結束本筆療程？',
      template: this.patientInfoDentalTreatmentEndTpl,
      templateData: item,
      fitContent: true,
      textYes: '結束療程',
      textNo: '取消',
      width: 760
    });
    if (confirmed !== true && confirmed !== 'true') {
      return;
    }

    try {
      const patientId = Number(this.patientInfoSelectedPatient.Id || 0);
      const updated = await this.registerService.SetDentalTreatmentDateEnd(patientId, item, dateEnd);
      if (!updated) {
        this.notification.showError('療程結束狀態更新失敗');
        return;
      }
      this.notification.showSuccess('療程已結束', true);
      await this.loadPatientInfoRegisterInfo(this.patientInfoSelectedPatient);
    } catch (ex) {
      this.notification.showError(this.handleErrorService.handleError(ex) || ex);
    }
  }

  getPatientInfoPeriodDate(value: any): string {
    return value || '';
  }

  private getPatientInfoPeriodExecutableDate(item?: RegisterPeriodItemDTO): Date | null {
    const value = `${item?.ExecutableDate || ''}`.trim();
    if (!value) {
      return null;
    }

    const parts = value.split(/[^\d]+/).filter(part => !!part);
    if (parts.length >= 3) {
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      const date = new Date(year < 1911 ? year + 1911 : year, month - 1, day);
      return isNaN(date.getTime()) ? null : date;
    }

    const digits = value.replace(/\D/g, '');
    if (digits.length >= 7) {
      const date = DateHelper.parseROCDate(digits);
      return isNaN(date.getTime()) ? null : date;
    }

    const validDate = this.getPatientInfoValidDate(value);
    if (validDate) {
      return validDate;
    }

    return null;
  }

  getPatientInfoPeriodRxCodes(item?: RegisterPeriodItemDTO): string {
    return (item?.RxCodes || []).filter(code => !!code).join(', ');
  }

  getPatientInfoClinicHistoryDate(item?: RegisterClinicHistoryDTO): string {
    return item?.RegDate || '';
  }

  getPatientInfoClinicHistoryDxCodes(item?: RegisterClinicHistoryDTO): string {
    return (item?.DxCodes || []).filter(code => !!code).join(', ');
  }

  async onPatientInfoClinicHistoryClick(item: RegisterClinicHistoryDTO, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item) {
      return;
    }

    const requestId = ++this.patientInfoSimpleHistRequestId;

    this.patientInfoSimpleHist = {
      visible: true,
      loading: true,
      error: '',
      regId: 0,
      source: item,
      data: null,
      ...this.createPatientInfoSimpleHistLayoutState()
    };

    try {
      const simpleHistResult = await this.loadPatientInfoSimpleHist(item);
      if (this.isDestroyed || requestId !== this.patientInfoSimpleHistRequestId) {
        return;
      }

      const layoutState = this.getPatientInfoSimpleHistLayoutState();
      this.patientInfoSimpleHist = {
        visible: true,
        loading: false,
        error: '',
        regId: simpleHistResult.regId,
        source: item,
        data: simpleHistResult.wholeHist,
        ...layoutState
      };
      this.cdr.detectChanges();
      /*
      return;

      const regIds = await this.histApi.GetRegIdList([Number(this.getPatientInfoClinicHistoryHistId(item))]);
      const regId = Number(regIds?.[0] || 0);
      if (!regId) {
        throw new Error('找不到這筆病歷對應的掛號資料');
      }

      const wholeHist = WholeHist.create(await this.histApi.getWholeHist({ regId }));
      if (this.isDestroyed) {
        return;
      }

      this.patientInfoSimpleHist = {
        visible: true,
        loading: false,
        error: '',
        regId,
        source: item,
        data: wholeHist
      };
      this.cdr.detectChanges();
      */
    } catch (ex) {
      if (this.isDestroyed || requestId !== this.patientInfoSimpleHistRequestId) {
        return;
      }
      const layoutState = this.getPatientInfoSimpleHistLayoutState();
      this.patientInfoSimpleHist = {
        visible: true,
        loading: false,
        error: this.getPatientInfoErrorMessage(ex) || '病歷資料載入失敗',
        regId: 0,
        source: item,
        data: null,
        ...layoutState
      };
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    }
  }

  private async loadPatientInfoSimpleHist(item: RegisterClinicHistoryDTO): Promise<{ regId: number, wholeHist: WholeHist }> {
    const directRegId = this.getPatientInfoClinicHistoryRegId(item);
    if (directRegId) {
      return await this.loadPatientInfoWholeHistByRegId(directRegId);
    }

    const histId = this.getPatientInfoClinicHistoryHistId(item);
    if (histId) {
      const regIdFromHistId = await this.getPatientInfoRegIdByHistId(histId);
      if (regIdFromHistId) {
        this.setPatientInfoClinicHistoryRegId(item, regIdFromHistId);
        return await this.loadPatientInfoWholeHistByRegId(regIdFromHistId);
      }

      const wholeHistFromHistId = await this.getPatientInfoWholeHistByHistId(histId);
      if (wholeHistFromHistId?.wholeHist) {
        this.setPatientInfoClinicHistoryRegId(item, wholeHistFromHistId.regId);
        return wholeHistFromHistId;
      }

      throw new Error('找不到這筆病歷對應的掛號資料');
    }

    const fallbackRegId = await this.findPatientInfoRegIdByClinicHistory(item);
    if (fallbackRegId) {
      this.setPatientInfoClinicHistoryRegId(item, fallbackRegId);
      return await this.loadPatientInfoWholeHistByRegId(fallbackRegId);
    }

    throw new Error('找不到這筆病歷對應的掛號資料');
  }

  private async loadPatientInfoWholeHistByRegId(regId: number): Promise<{ regId: number, wholeHist: WholeHist }> {
    const wholeHist = WholeHist.create(await this.histApi.getWholeHist({ regId }));
    const resolvedRegId = Number(wholeHist?.Register?.Id || regId || 0);
    if (!resolvedRegId) {
      throw new Error('找不到這筆病歷對應的掛號資料');
    }

    return { regId: resolvedRegId, wholeHist };
  }

  private async getPatientInfoRegIdByHistId(histId: number): Promise<number> {
    try {
      const regIds = await this.histApi.GetRegIdList([histId]);
      return Number(regIds?.[0] || 0);
    } catch (ex) {
      return 0;
    }
  }

  private async getPatientInfoWholeHistByHistId(histId: number): Promise<{ regId: number, wholeHist: WholeHist } | null> {
    try {
      const wholeHists = await this.histApi.getBy([histId]);
      const wholeHist = WholeHist.create((wholeHists || [])[0]);
      const regId = Number(wholeHist?.Register?.Id || 0);
      return regId ? { regId, wholeHist } : null;
    } catch (ex) {
      return null;
    }
  }

  private async findPatientInfoRegIdByClinicHistory(item: RegisterClinicHistoryDTO): Promise<number> {
    const patientId = Number(this.patientInfoSelectedPatient?.Id || 0);
    if (!patientId) {
      return 0;
    }

    try {
      const targetDate = this.getPatientInfoValidDate(item?.RegDate);
      const list = await this.histApi.GetHistViewList(patientId);
      const candidates = (list || [])
        .filter(hist => !targetDate || this.isSamePatientInfoDate(this.getPatientInfoValidDate(hist?.RegDate), targetDate))
        .sort((a, b) => this.getPatientInfoDateTime(b?.RegDate) - this.getPatientInfoDateTime(a?.RegDate));

      if (candidates.length === 1) {
        return Number(candidates[0]?.Id || 0);
      }

      for (const candidate of candidates) {
        const regId = Number(candidate?.Id || 0);
        if (!regId) {
          continue;
        }

        const wholeHist = WholeHist.create(await this.histApi.getWholeHist({ regId }));
        if (this.isPatientInfoWholeHistMatchingClinicHistory(wholeHist, item)) {
          return Number(wholeHist?.Register?.Id || regId || 0);
        }
      }

      return Number(candidates[0]?.Id || 0);
    } catch (ex) {
      return 0;
    }
  }

  getPatientInfoClinicHistoryRegId(item?: RegisterClinicHistoryDTO): number {
    const raw = item as any;
    return Number(raw?.RegId || raw?.RegisterId || raw?.RegisterID || 0);
  }

  getPatientInfoClinicHistoryHistId(item?: RegisterClinicHistoryDTO): number {
    const raw = item as any;
    return Number(raw?.HistId || raw?.HistoryId || raw?.HistID || 0);
  }

  getPatientInfoClinicHistoryHRxId(item?: RegisterClinicHistoryDTO): number {
    const raw = item as any;
    return Number(raw?.HRxId || raw?.HrxId || raw?.HRXId || 0);
  }

  private setPatientInfoClinicHistoryRegId(item: RegisterClinicHistoryDTO, regId: number) {
    if (item && regId) {
      (item as any).RegId = regId;
    }
  }

  private isPatientInfoWholeHistMatchingClinicHistory(wholeHist: WholeHist, item: RegisterClinicHistoryDTO): boolean {
    const targetCode = this.normalizePatientInfoCode(item?.StdCode || item?.RxCode);
    const targetTooth = this.normalizePatientInfoCode(item?.ToothPos);
    const targetDxCodes = (item?.DxCodes || []).map(code => this.normalizePatientInfoCode(code)).filter(code => !!code);
    const orders = wholeHist?.Hist?.HRxs || [];
    const diags = this.getPatientInfoSimpleHistDiagsFromHist(wholeHist?.Hist).map(diag => this.normalizePatientInfoCode(diag.code));

    const orderCodeMatched = !targetCode || orders.some(order =>
      this.normalizePatientInfoCode(order?.StdCode) === targetCode
      || this.normalizePatientInfoCode(order?.RxCode) === targetCode
    );
    const toothMatched = !targetTooth || orders.some(order =>
      this.normalizePatientInfoCode(order?.ToothPos) === targetTooth
    );
    const dxMatched = !targetDxCodes.length || targetDxCodes.some(code => diags.indexOf(code) >= 0);

    if (targetCode) {
      return orderCodeMatched && (!targetTooth || toothMatched || !orders.length);
    }

    return dxMatched;
  }

  private normalizePatientInfoCode(value: any): string {
    return `${value || ''}`.trim().toUpperCase();
  }

  closePatientInfoSimpleHist(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.patientInfoSimpleHistRequestId++;
    this.removePatientInfoSimpleHistDocumentListeners();
    this.patientInfoSimpleHist = {
      visible: false,
      loading: false,
      error: '',
      regId: 0,
      source: null,
      data: null,
      ...this.createPatientInfoSimpleHistLayoutState(false)
    };
  }

  getPatientInfoSimpleHistTitle(): string {
    const hist = this.patientInfoSimpleHist.data;
    const patientName = hist?.Patient?.CName || this.getPatientInfoDisplayName(this.patientInfoSelectedPatient);
    const regDate = this.formatPatientInfoMinguoDate(hist?.Register?.RegDate) || this.getPatientInfoClinicHistoryDate(this.patientInfoSimpleHist.source);
    return [patientName, regDate]
      .filter(value => !!value)
      .join(' / ');
  }

  getPatientInfoSimpleHistRegId(): number {
    return Number(
      this.patientInfoSimpleHist.regId
      || this.getPatientInfoClinicHistoryRegId(this.patientInfoSimpleHist.source)
      || this.patientInfoSimpleHist.data?.Register?.Id
      || 0
    );
  }

  getPatientInfoSimpleHistResizeHandles(): string[] {
    return this.patientInfoFloatingResizeHandles;
  }

  getPatientInfoSimpleHistOverlayStyle(): { [key: string]: string } {
    const viewport = this.getPatientInfoViewport();
    const area = this.getPatientInfoSimpleHistAvailableArea();
    return {
      top: `${area.top}px`,
      left: `${area.left}px`,
      right: `${Math.max(0, viewport.width - area.right)}px`,
      bottom: `${Math.max(0, viewport.height - area.bottom)}px`
    };
  }

  getPatientInfoSimpleHistDialogStyle(): { [key: string]: string } {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    const rect = this.getPatientInfoSimpleHistVisualRect();
    return {
      left: `${Math.round(rect.left - area.left)}px`,
      top: `${Math.round(rect.top - area.top)}px`,
      width: `${Math.round(rect.width)}px`,
      height: `${Math.round(rect.height)}px`
    };
  }

  onPatientInfoSimpleHistHeaderPointerDown(event: PointerEvent) {
    const target = event.target as HTMLElement;
    if (!this.patientInfoSimpleHist.visible || target.closest('button')) {
      return;
    }

    event.preventDefault();
    this.patientInfoSimpleHist.dragging = true;
    this.patientInfoSimpleHist.moved = false;
    this.patientInfoSimpleHist.startX = event.clientX;
    this.patientInfoSimpleHist.startY = event.clientY;
    this.patientInfoSimpleHist.startRect = this.getPatientInfoSimpleHistVisualRect();
    this.patientInfoSimpleHist.startOffsetX = event.clientX - this.patientInfoSimpleHist.startRect.left;
    this.patientInfoSimpleHist.startOffsetY = event.clientY - this.patientInfoSimpleHist.startRect.top;
    this.addPatientInfoSimpleHistDocumentListeners();
  }

  onPatientInfoSimpleHistResizePointerDown(event: PointerEvent, direction: string) {
    if (!this.patientInfoSimpleHist.visible) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.patientInfoSimpleHist.resizing = true;
    this.patientInfoSimpleHist.resizeDir = direction;
    this.patientInfoSimpleHist.resizeHoverDir = direction;
    this.patientInfoSimpleHist.startX = event.clientX;
    this.patientInfoSimpleHist.startY = event.clientY;
    this.patientInfoSimpleHist.startRect = this.getPatientInfoSimpleHistVisualRect();
    this.addPatientInfoSimpleHistDocumentListeners();
  }

  onPatientInfoSimpleHistResizeHandleEnter(direction: string) {
    if (!this.patientInfoSimpleHist.resizing) {
      this.patientInfoSimpleHist.resizeHoverDir = direction;
    }
  }

  onPatientInfoSimpleHistResizeHandleLeave(direction: string) {
    if (!this.patientInfoSimpleHist.resizing && this.patientInfoSimpleHist.resizeHoverDir === direction) {
      this.patientInfoSimpleHist.resizeHoverDir = '';
    }
  }

  getPatientInfoSimpleHistPinTitle(): string {
    return this.patientInfoSimpleHist.pinned ? '取消釘選' : '釘選';
  }

  togglePatientInfoSimpleHistPin(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.patientInfoSimpleHist.visible) {
      return;
    }

    this.patientInfoSimpleHist.maskVisible = false;
    this.patientInfoSimpleHist.moved = true;
    if (this.patientInfoSimpleHist.pinned) {
      const side = this.patientInfoSimpleHist.pinSide;
      const rect = { ...(this.patientInfoSimpleHist.floatingRectBeforePin || this.patientInfoSimpleHist.rect) };
      if (side === 'left' || side === 'right') {
        rect.width = this.patientInfoSimpleHist.pinnedSize;
      } else if (side === 'top' || side === 'bottom') {
        rect.height = this.patientInfoSimpleHist.pinnedSize;
      }
      this.patientInfoSimpleHist.rect = this.getClampedPatientInfoSimpleHistRect(rect);
      this.patientInfoSimpleHist.pinned = false;
      this.patientInfoSimpleHist.pinSide = '';
      this.patientInfoSimpleHist.pinnedSize = 0;
      this.savePatientInfoSimpleHistLayout();
      return;
    }

    const rect = this.getPatientInfoSimpleHistVisualRect();
    const side = this.getClosestPatientInfoSimpleHistEdge(rect);
    this.pinPatientInfoSimpleHistToSide(side, rect);
    this.savePatientInfoSimpleHistLayout();
  }

  openPatientInfoSimpleHistFullRecord(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    const regId = this.getPatientInfoSimpleHistRegId();
    if (!regId) {
      return;
    }

    const path = this.routeInfo.makeFullUrl('/hist/record-dental');
    this.closePatientInfoSimpleHist();
    if (this.getPatientInfoSimpleHistRouteKey() === '/hist/record-dental') {
      const targetPath = `/${path.replace(/^\/+/, '')};regId=${encodeURIComponent(`${regId}`)};h=0`;
      (window as any).__patientInfoSkipRecordDentalBeforeUnload = true;
      window.location.assign(targetPath);
      return;
    }
    setTimeout(() => {
      this.router.navigate([path, { regId, h: '0' }]);
    }, 0);
  }

  getPatientInfoSimpleHistRemark(): string {
    return this.patientInfoSimpleHist.data?.Patient?.Remark || '';
  }

  getPatientInfoSimpleHistCc(): string {
    return this.patientInfoSimpleHist.data?.Hist?.CC || '';
  }

  getPatientInfoSimpleHistPe(): string {
    return this.patientInfoSimpleHist.data?.Hist?.PE || '';
  }

  getPatientInfoSimpleHistDiags(): { no: number, code: string, name: string }[] {
    return this.getPatientInfoSimpleHistDiagsFromHist(this.patientInfoSimpleHist.data?.Hist);
  }

  getPatientInfoSimpleHistDiagLabel(diag: { code: string, name: string }): string {
    return [diag?.code, diag?.name].filter(value => !!value).join(' ');
  }

  private getPatientInfoSimpleHistDiagsFromHist(hist: any): { no: number, code: string, name: string }[] {
    if (!hist) {
      return [];
    }

    const histAny = hist as any;
    const diags = hist.Diags?.length
      ? hist.Diags
      : [1, 2, 3, 4, 5].map(no => ({
        no,
        code: histAny[`Dx${no}`] || '',
        name: histAny[`Dx${no}Name`] || ''
      }));

    return diags
      .map((diag: any, index: number) => ({
        no: Number(diag?.no || index + 1),
        code: `${diag?.code || ''}`.trim(),
        name: `${diag?.name || ''}`.trim()
      }))
      .filter(diag => !!diag.code || !!diag.name)
      .slice(0, 5);
  }

  getPatientInfoSimpleHistOrders(group: 'examFee' | 'treatment' | 'prescription'): HistOrder[] {
    const orders = this.patientInfoSimpleHist.data?.Hist?.HRxs || [];
    return orders.filter(order => {
      if (group === 'examFee') {
        return Number(order?.RxType) === OrderTypeEnum.T1_ConsultationFee;
      }

      const isDentalTreatment = this.isPatientInfoDentalTreatmentOrder(order);
      if (group === 'treatment') {
        return isDentalTreatment;
      }

      return Number(order?.RxType) !== OrderTypeEnum.T1_ConsultationFee && !isDentalTreatment;
    });
  }

  getPatientInfoSimpleHistOrderCode(order?: HistOrder): string {
    return order?.StdCode || order?.RxCode || '';
  }

  getPatientInfoSimpleHistOrderName(order?: HistOrder): string {
    return order?.ProdName || order?.OrderCodeName || order?.Rx?.ProdName || order?.Rx?.ChineseName || '';
  }

  getPatientInfoSimpleHistOrderQty(order?: HistOrder): string {
    const value = order?.DentalQty ?? order?.QTY ?? order?.TotalDose ?? '';
    return value === null || value === undefined ? '' : `${value}`;
  }

  getPatientInfoSimpleHistOrderTooth(order?: HistOrder): string {
    return [order?.ToothPos, order?.ToothFace ? `(${order.ToothFace})` : ''].filter(value => !!value).join('');
  }

  hasPatientInfoSimpleHistOrders(group: 'examFee' | 'treatment' | 'prescription'): boolean {
    return this.getPatientInfoSimpleHistOrders(group).length > 0;
  }

  private isPatientInfoDentalTreatmentOrder(order?: HistOrder): boolean {
    return [
      OrderTypeEnum.T5_InjectionServiceFee,
      OrderTypeEnum.T6_Lab,
      OrderTypeEnum.T7_XRay,
      OrderTypeEnum.T8_RehabilitationFee,
      OrderTypeEnum.T9_TreatmentFee,
      OrderTypeEnum.T10_SurgeryFee,
      OrderTypeEnum.T11_AnesthesiaFee
    ].includes(Number(order?.RxType) as OrderTypeEnum);
  }

  private getPatientInfoErrorMessage(error: any): string {
    return error?.error?.Message || error?.error?.message || error?.Message || error?.message || `${error || ''}`;
  }

  getPatientInfoReserveRecordDate(item?: ReserveItem): string {
    return this.formatPatientInfoMinguoDate(item?.AppointmentDate);
  }

  getPatientInfoReserveRecordTimeRange(item?: ReserveItem): string {
    const start = item?.CheckInTime || this.getPatientInfoReserveRecordStartTime(item);
    const appointmentDate = this.getPatientInfoValidDate(item?.AppointmentDate);
    if (!start || !appointmentDate) {
      return start || '';
    }

    const end = DateHelper.getHourMinTime(DateHelper.addMinute(appointmentDate, Number(item?.EstimatedTime || 0) || 15), true);
    return `${start}~${end}`;
  }

  getPatientInfoReserveRecordTitle(item?: ReserveItem): string {
    return item?.ReserveMatters || this.getPatientInfoRoomName(item?.RoomCode) || item?.DoctorName || '約診';
  }

  getPatientInfoReserveRecordMeta(item?: ReserveItem): string {
    return [
      this.getPatientInfoReserveRecordDate(item),
      this.getPatientInfoReserveRecordTimeRange(item),
      item?.DoctorName,
      this.getPatientInfoRoomName(item?.RoomCode)
    ].filter(value => !!value).join(' ');
  }

  getPatientInfoRoomName(roomCode?: string): string {
    const code = `${roomCode || ''}`.trim();
    if (!code) {
      return '';
    }

    const matched = this.patientInfoRoomOptions.find(option => `${option.value}`.trim() === code);
    return matched?.text || code;
  }

  isPatientInfoReserveRecordExpiredToday(item?: ReserveItem): boolean {
    const appointmentDate = this.getPatientInfoValidDate(item?.AppointmentDate);
    if (!appointmentDate) {
      return false;
    }

    const now = new Date();
    return this.isSamePatientInfoDate(appointmentDate, now) && appointmentDate.getTime() < now.getTime();
  }

  onPatientInfoReserveRecordClick(item: ReserveItem, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item) {
      return;
    }

    this.patientInfoWindowService.requestReserveRecordNavigation({
      patient: this.patientInfoSelectedPatient,
      patientId: Number(this.patientInfoSelectedPatient?.Id || item.PatientId || 0),
      reserveItem: item
    });
  }

  onPatientInfoReservePeriodItem(item: RegisterPeriodItemDTO, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item || !this.patientInfoSelectedPatient) {
      return;
    }

    const targetDate = !item.IsAllowExecute ? this.getPatientInfoPeriodExecutableDate(item) : null;
    this.patientInfoWindowService.requestReserveRecordNavigation({
      patient: this.patientInfoSelectedPatient,
      patientId: Number(this.patientInfoSelectedPatient.Id || 0),
      reserveMatters: item.PeriodItemName || '',
      targetDate: targetDate || undefined
    });
  }

  canPagePatientInfoClinicHistory(direction: -1 | 1): boolean {
    const nextPage = this.patientInfoClinicHistoryPage + direction;
    return nextPage >= 1 && nextPage <= this.patientInfoClinicHistoryTotalPages;
  }

  pagePatientInfoClinicHistory(direction: -1 | 1) {
    if (!this.canPagePatientInfoClinicHistory(direction)) {
      return;
    }

    this.patientInfoClinicHistoryPage += direction;
  }

  getPatientInfoDisplayName(patient?: PatientListDto): string {
    return patient?.Name || patient?.FullName || '';
  }

  getPatientInfoPrimaryNoLabel(patient?: PatientListDto): string {
    const kcstmr = this.getPatientInfoValue(patient, 'Kcstmr');
    if (kcstmr) {
      return `自訂碼 ${kcstmr}`;
    }

    const patientNo = this.getPatientInfoValue(patient, 'PatientNo');
    return patientNo ? `病歷號 ${patientNo}` : '';
  }

  getPatientInfoBirthday(patient?: PatientListDto): string {
    return patient?.Birthday ? this.formatRocBirthday(patient.Birthday) : '';
  }

  getPatientInfoPrimaryPhone(patient?: PatientListDto): string {
    return this.getPatientInfoMobile(patient) || this.getPatientInfoHomePhone(patient);
  }

  getPatientInfoSexIcon(patient?: PatientListDto): string {
    const sex = this.getPatientInfoValue(patient, 'Sex');
    if (sex === '1' || sex === '男' || sex === '男性') {
      return 'male';
    }

    if (sex === '0' || sex === '女' || sex === '女性') {
      return 'female';
    }

    return '';
  }

  getPatientInfoSexClass(patient?: PatientListDto): string {
    const icon = this.getPatientInfoSexIcon(patient);
    return icon ? `patient-info-sex-${icon}` : '';
  }

  getPatientInfoSexTitle(patient?: PatientListDto): string {
    const icon = this.getPatientInfoSexIcon(patient);
    if (icon === 'male') {
      return '男性';
    }

    if (icon === 'female') {
      return '女性';
    }

    return '';
  }

  getPatientInfoAgeText(patient?: PatientListDto): string {
    const birthday = DateHelper.createDate(patient?.Birthday);
    if (!birthday) {
      return '';
    }

    const today = new Date();
    let years = today.getFullYear() - birthday.getFullYear();
    const hasBirthdayPassed = today.getMonth() > birthday.getMonth()
      || (today.getMonth() === birthday.getMonth() && today.getDate() >= birthday.getDate());
    if (!hasBirthdayPassed) {
      years -= 1;
    }

    let months = (today.getFullYear() - birthday.getFullYear()) * 12 + today.getMonth() - birthday.getMonth();
    if (today.getDate() < birthday.getDate()) {
      months -= 1;
    }
    months = Math.max(0, months);

    this.patientInfoCurrentAgeClass = '';
    if (years >= 12) {
      if (years >= 65) {
        this.patientInfoCurrentAgeClass = 'patient-info-current-age-65';
      }
      return `${years}歲`;
    }

    if (years > 0) {
      const remainingMonths = Math.max(0, months - years * 12);
      return remainingMonths > 0 ? `${years}歲${remainingMonths}個月` : `${years}歲`;
    }

    return months > 0 ? `${months}個月` : '未滿1個月';
  }

  getPatientInfoCId(patient?: PatientListDto): string {
    return this.getPatientInfoValue(patient, 'CId');
  }

  getPatientInfoMobile(patient?: PatientListDto): string {
    return this.getPatientInfoValue(patient, 'Mobile') || this.getPatientInfoValue(patient, 'CellPhone');
  }

  getPatientInfoHomePhone(patient?: PatientListDto): string {
    const phoneArea = this.getPatientInfoValue(patient, 'PhoneArea');
    const phone = this.getPatientInfoValue(patient, 'Phone');
    if (phone) {
      return `${phoneArea}${phone}`.trim();
    }

    return this.getPatientInfoValue(patient, 'PhoneNo') || this.getPatientInfoValue(patient, 'PhoneHome');
  }

  getPatientInfoSpecialIdentityText(patient?: PatientListDto): string {
    const spcIdentity = this.getPatientInfoValue(patient, 'SpcIdentity');
    if (!spcIdentity) {
      return '';
    }

    const matchedOptions = this.getPatientInfoSpcIdentityTexts(spcIdentity);
    if (matchedOptions.length || this.isPatientInfoBitString(spcIdentity)) {
      return matchedOptions.join('、');
    }

    return spcIdentity;
  }

  private refreshPatientInfoAlertTags() {
    this.patientInfoAlertTags = this.buildPatientInfoAlertTags(this.patientInfoSelectedPatient);
    this.publishPetContext();
  }

  private petDetailPending = false;
  private petSelectionPending = false;

  private publishPetContext(): void {
    if (this.isDestroyed || this.petSelectionPending || this.petDetailPending || this.patientInfoRegisterInfoLoading) return;
    const patient = this.patientInfoSelectedPatient;
    const patientId = Number(patient?.Id || 0);
    const reference = this.patientInfoPetBirthdayReference;
    const referenceDate = reference && reference.patientId === patientId
      ? this.getPatientInfoValidDate(reference.referenceDate)
      : null;
    const kinds = this.patientInfoAlertTags.map(tag => tag.cssClass.replace('patient-info-alert-', '') as PetAlertKind);
    this.petContext.update('patient-info-window', patientId, kinds,
      petBirthdayNearby(DateHelper.createDate(patient?.Birthday), referenceDate ? [referenceDate] : []),
      // This fingerprint stays in browser memory; only categories go on the wire.
      JSON.stringify(this.patientInfoAlertTags));
  }

  private buildPatientInfoAlertTags(patient?: PatientListDto): PatientInfoAlertTag[] {
    if (!patient) {
      return [];
    }

    const tags: PatientInfoAlertTag[] = [];
    const allergyTag = this.buildPatientInfoDrugAllergyTag(this.getPatientInfoValue(patient, 'DrugAllergy'));
    if (allergyTag) {
      tags.push(allergyTag);
    }

    tags.push(...this.buildPatientInfoRemarkTags(this.getPatientInfoValue(patient, 'Remark')));

    const specialIdentity = this.getPatientInfoSpecialIdentityText(patient);
    if (specialIdentity) {
      tags.push({
        label: `身份 ${specialIdentity}`,
        tooltip: `特殊身分：${specialIdentity}`,
        cssClass: 'patient-info-alert-special-identity'
      });
    }

    return tags;
  }

  private buildPatientInfoDrugAllergyTag(rawValue: string): PatientInfoAlertTag | null {
    const raw = (rawValue || '').trim();
    if (!raw) {
      return null;
    }

    const parts = raw.split(';');
    const drugCodes = (parts[0] || '')
      .split(',')
      .map(code => code.trim())
      .filter(code => !!code);
    const hasOther = parts.slice(1).some(part => !!part.trim());
    if (!drugCodes.length && !hasOther) {
      return null;
    }

    const summaryItems = drugCodes.slice();
    if (hasOther) {
      summaryItems.push('其他');
    }

    const tooltipLines: string[] = [];
    if (drugCodes.length) {
      tooltipLines.push(`[藥品代碼]：${drugCodes.join(',')}`);
    }
    if ((parts[1] || '').trim()) {
      tooltipLines.push(`[ATC碼]：${parts[1].trim()}`);
    }
    if ((parts[2] || '').trim()) {
      tooltipLines.push(`[過敏藥群組]：${parts[2].trim()}`);
    }
    const otherAllergy = parts.slice(3).join(';').trim();
    if (otherAllergy) {
      tooltipLines.push(`[其他]：${otherAllergy}`);
    }

    return {
      label: `過敏 ${summaryItems.join('、')}`,
      tooltip: tooltipLines.join('\n'),
      cssClass: 'patient-info-alert-drug-allergy'
    };
  }

  private buildPatientInfoRemarkTags(rawValue: string): PatientInfoAlertTag[] {
    const remark = rawValue || '';
    if (!remark.trim()) {
      return [];
    }

    const definitions = [
      { name: '既往病史', label: '病史', cssClass: 'patient-info-alert-past-history' },
      { name: '家族病史', label: '家族', cssClass: 'patient-info-alert-family-history' },
      { name: '手術病史', label: '手術', cssClass: 'patient-info-alert-surgery-history' }
    ];
    const values: { [name: string]: string[] } = {};
    const sourceLines: { [name: string]: string[] } = {};
    definitions.forEach(definition => {
      values[definition.name] = [];
      sourceLines[definition.name] = [];
    });

    remark.split(/\r?\n/).forEach(line => {
      const matched = line.match(/^\s*\[(既往病史|家族病史|手術病史)\]\s*[：:]\s*(.*)$/);
      if (!matched) {
        return;
      }

      const name = matched[1];
      const value = (matched[2] || '').trim();
      if (!value) {
        return;
      }

      values[name].push(value);
      sourceLines[name].push(line.trim());
    });

    return definitions
      .filter(definition => values[definition.name].length > 0)
      .map(definition => ({
        label: `${definition.label} ${values[definition.name].join('；').replace(/[,，]\s*/g, '、')}`,
        tooltip: sourceLines[definition.name].join('\n'),
        cssClass: definition.cssClass
      }));
  }

  isPatientInfoSelected(patient?: PatientListDto): boolean {
    if (!patient || !this.patientInfoSelectedPatient) {
      return false;
    }

    return this.getPatientInfoIdentity(patient) === this.getPatientInfoIdentity(this.patientInfoSelectedPatient);
  }

  onPatientInfoEditClick(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.enableEdit || !this.patientInfoSelectedPatient) {
      return;
    }

    this.editPatient.emit({
      patient: this.patientInfoSelectedPatient,
      registerItem: this.selectedRegisterItem
    });
  }

  onPatientInfoReserveClick(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.patientInfoSelectedPatient) {
      return;
    }

    this.reservePatient.emit({
      patient: this.patientInfoSelectedPatient,
      registerItem: this.selectedRegisterItem
    });
  }

  onPatientInfoSearchKeywordChange(value: string) {
    this.patientInfoSearchKeyword = value || '';
    this.patientInfoSearchSuggestionsVisible = true;
    this.updatePatientInfoSearchSuggestions();
    this.savePatientInfoMemory();
  }

  onPatientInfoSearchInputFocus() {
    this.patientInfoSearchSuggestionsVisible = true;
    this.updatePatientInfoSearchSuggestions();
  }

  onPatientInfoSearchInputBlur() {
    setTimeout(() => {
      this.patientInfoSearchSuggestionsVisible = false;
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    }, 120);
  }

  selectPatientInfoSearchSuggestion(keyword: string) {
    this.patientInfoSearchKeyword = keyword || '';
    this.patientInfoSearchSuggestionsVisible = false;
    this.updatePatientInfoSearchSuggestions();
    this.onPatientInfoSearchSubmit();
  }

  onPatientInfoSearchResultsToggle(event: Event) {
    const details = event.target as HTMLDetailsElement;
    this.patientInfoSearchResultsCollapsed = !details?.open;
    this.animatePatientInfoDetails(details);
  }

  onPatientInfoSectionToggle(section: PatientInfoSectionKey, event: Event) {
    const details = event.target as HTMLDetailsElement;
    this.patientInfoSectionExpanded[section] = !!details?.open;
    this.animatePatientInfoDetails(details);
  }

  private bindPatientInfoRoutePolicy() {
    this.patientInfoRouteSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.applyPatientInfoRouteSectionPolicy();
      }
    });
  }

  private applyPatientInfoRouteSectionPolicy(force = false) {
    const isRegReserve = this.isPatientInfoRegReserveRoute();
    if (isRegReserve && (force || !this.lastPatientInfoRegReserveRoute)) {
      this.patientInfoSectionExpanded = {
        ...this.patientInfoSectionExpanded,
        reserveRecords: true
      };
    }
    this.lastPatientInfoRegReserveRoute = isRegReserve;
  }

  private collapsePatientInfoSections() {
    this.patientInfoSectionExpanded = {
      family: false,
      activeTreatments: false,
      periodItems: false,
      reserveRecords: false,
      clinicHistories: false
    };
  }

  private collapsePatientInfoSearchResults() {
    if (this.patientInfoSearchResults.length || this.patientInfoSearchResultsKeyword) {
      this.patientInfoSearchResultsCollapsed = true;
    }
  }

  private applyPatientInfoSelectionSectionPolicy(source: PatientInfoSelectionSource = 'default') {
    if (source === 'preserve') {
      return;
    }

    const keepFamilyOpen = source === 'family' ? this.patientInfoSectionExpanded.family : false;
    this.patientInfoSectionExpanded = {
      family: keepFamilyOpen,
      activeTreatments: true,
      periodItems: true,
      reserveRecords: true,
      clinicHistories: true
    };
  }

  private isPatientInfoRegReserveRoute(): boolean {
    const url = this.router?.url || window.location.pathname || '';
    return url.split('?')[0].split('#')[0].indexOf('/registers/reg-reserve') >= 0;
  }

  private animatePatientInfoDetails(details?: HTMLDetailsElement) {
    details?.classList.remove('is-expanding', 'is-collapsing');
    if (!details) {
      return;
    }

    void details.offsetWidth;
    details.classList.add(details.open ? 'is-expanding' : 'is-collapsing');
    window.setTimeout(() => {
      details.classList.remove('is-expanding', 'is-collapsing');
    }, 220);
  }

  async onPatientInfoSearchSubmit(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const keyword = (this.patientInfoSearchKeyword || '').trim();
    if (!keyword || this.patientInfoSearchLoading) {
      return;
    }

    this.patientInfoSearchKeyword = keyword;
    this.patientInfoSearchSuggestionsVisible = false;
    this.collapsePatientInfoSections();
    this.rememberPatientInfoSearchKeyword(keyword);
    this.savePatientInfoMemory();
    this.patientInfoSearchLoading = true;
    this.patientInfoSearchError = '';

    try {
      const query = this.createPatientInfoSearchQuery(keyword);
      const result = await this.patientApi.GetAll(query);
      const rows = this.normalizePatientInfoRows(result?.Data?.data || []);
      this.patientInfoSearchResultsKeyword = keyword;
      this.patientInfoSearchResults = rows;
      this.patientInfoSearchResultsCollapsed = false;
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
      this.scrollPatientInfo();
      this.patientInfoSearchError = rows.length > 0 ? '' : '查無患者資料';
      this.savePatientInfoMemory();
    } catch (ex) {
      const errorMsg = this.handleErrorService.handleError(ex);
      this.patientInfoSearchError = errorMsg || '患者檢索失敗';
      this.notification.showError(this.patientInfoSearchError);
    } finally {
      this.patientInfoSearchLoading = false;
      this.updatePatientInfoSearchSuggestions();
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    }
  }

  selectPatientInfoSearchResult(patient: PatientListDto) {
    if (!patient) {
      return;
    }

    this.setPatientInfoSelectedPatient(patient, true, true, 'default');
    this.collapsePatientInfoSearchResults();
    // this.scrollPatientInfoCurrentIntoView();
    this.scrollPatientInfo();
  }

  selectPatientInfoFamilyMember(patient: PatientListDto) {
    if (!patient) {
      return;
    }

    this.setPatientInfoSelectedPatient(patient, true, true, 'family');
    this.scrollPatientInfo();
  }

  getPatientInfoWindowStyle(): { [key: string]: string } {
    const resizeShadowStyle = this.getPatientInfoResizeShadowStyle();

    if (this.patientInfoWindow.minimized) {
      const rect = this.patientInfoWindow.pinned && this.patientInfoWindow.pinSide
        ? this.getPatientInfoPinnedMinimizedRect(this.patientInfoWindow.pinSide)
        : this.getClampedPatientInfoMinimizedRect(this.patientInfoWindow.minimizedRect);
      return {
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        ...resizeShadowStyle
      };
    }

    if (this.patientInfoWindow.pinned) {
      const size = this.getClampedPatientInfoPinnedSize(this.patientInfoWindow.pinSide, this.patientInfoWindow.pinnedSize);
      const area = this.getPatientInfoAvailableArea();
      switch (this.patientInfoWindow.pinSide) {
        case 'left':
          return { left: '0px', top: `${area.top}px`, width: `${size}px`, height: `${area.height}px`, ...resizeShadowStyle };
        case 'right':
          return { left: `${area.width - size}px`, top: `${area.top}px`, width: `${size}px`, height: `${area.height}px`, ...resizeShadowStyle };
        case 'top':
          return { left: '0px', top: `${area.top}px`, width: '100vw', height: `${size}px`, ...resizeShadowStyle };
        case 'bottom':
          return { left: '0px', top: `${area.bottom - size}px`, width: '100vw', height: `${size}px`, ...resizeShadowStyle };
      }
    }

    const rect = this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    return {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      ...resizeShadowStyle
    };
  }

  getPatientInfoPinTitle(): string {
    return this.patientInfoWindow.pinned ? '取消釘選' : '釘選';
  }

  getPatientInfoMinimizeTitle(): string {
    return this.patientInfoWindow.minimized ? '還原' : '縮小';
  }

  onPatientInfoHeaderPointerDown(event: PointerEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('.patient-info-action') || target.closest('.patient-info-current-edit')) {
      return;
    }

    event.preventDefault();
    this.patientInfoWindow.dragging = true;
    this.patientInfoWindow.moved = false;
    this.patientInfoWindow.startX = event.clientX;
    this.patientInfoWindow.startY = event.clientY;
    this.patientInfoWindow.startPinnedSize = this.patientInfoWindow.pinnedSize;
    this.patientInfoWindow.startPinSide = this.patientInfoWindow.pinSide;
    const visualRect = this.getPatientInfoCurrentVisualRect();
    this.patientInfoWindow.startOffsetX = event.clientX - visualRect.left;
    this.patientInfoWindow.startOffsetY = event.clientY - visualRect.top;
    this.patientInfoWindow.startRect = this.patientInfoWindow.minimized
      ? this.getClampedPatientInfoMinimizedRect(this.patientInfoWindow.minimizedRect)
      : this.patientInfoWindow.pinned
        ? visualRect
        : this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    this.addPatientInfoDocumentListeners();
  }

  onPatientInfoResizePointerDown(event: PointerEvent, direction: string) {
    if (this.patientInfoWindow.minimized) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.patientInfoWindow.resizing = true;
    this.patientInfoWindow.resizeDir = direction;
    this.patientInfoWindow.resizeHoverDir = direction;
    this.patientInfoWindow.startX = event.clientX;
    this.patientInfoWindow.startY = event.clientY;
    this.patientInfoWindow.startRect = this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    this.patientInfoWindow.startPinnedSize = this.patientInfoWindow.pinnedSize;
    this.resizeStateChange.emit(this.patientInfoWindow.pinned);
    this.addPatientInfoDocumentListeners();
  }

  onPatientInfoResizeHandleEnter(direction: string) {
    if (!this.patientInfoWindow.resizing) {
      this.patientInfoWindow.resizeHoverDir = direction;
    }
  }

  onPatientInfoResizeHandleLeave(direction: string) {
    if (!this.patientInfoWindow.resizing && this.patientInfoWindow.resizeHoverDir === direction) {
      this.patientInfoWindow.resizeHoverDir = '';
    }
  }

  togglePatientInfoMinimize(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.patientInfoWindow.minimized) {
      this.restorePatientInfoWindow();
      return;
    }

    const visualRect = this.getPatientInfoCurrentVisualRect();
    const minimizedRect = this.patientInfoWindow.pinned && this.patientInfoWindow.pinSide
      ? this.getPatientInfoPinnedMinimizedRect(this.patientInfoWindow.pinSide)
      : this.getPatientInfoMinimizedTargetRect(visualRect, this.patientInfoWindow.pinSide);

    this.patientInfoWindow.restoreState = {
      pinned: this.patientInfoWindow.pinned,
      pinSide: this.patientInfoWindow.pinSide,
      pinnedSize: this.patientInfoWindow.pinnedSize,
      rect: { ...this.patientInfoWindow.rect }
    };
    this.patientInfoWindow.minimizedRect = minimizedRect;
    this.patientInfoWindow.minimized = true;
    this.savePatientInfoMemory();
    this.emitLayoutChange();
  }

  togglePatientInfoPin(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.patientInfoWindow.minimized) {
      this.restorePatientInfoWindow();
    }

    if (this.patientInfoWindow.pinned) {
      this.unpinPatientInfoWindow();
      return;
    }

    const rect = this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    const side = this.getClosestPatientInfoEdge(rect);
    this.pinPatientInfoWindowToSide(side, rect);
  }

  private syncSelectedPatientInput() {
    if (!this.selectedPatient) {
      return;
    }

    if (this.getPatientInfoIdentity(this.selectedPatient) === this.getPatientInfoIdentity(this.patientInfoSelectedPatient)) {
      return;
    }

    this.setPatientInfoSelectedPatient(this.selectedPatient, false);
  }

  private syncRegisterItemInput(force: boolean) {
    if (!this.selectedRegisterItem?.PatientId && !this.selectedRegisterItem?.PatientName) {
      return;
    }

    if (!force && this.patientInfoSelectedPatient) {
      return;
    }

    this.setPatientInfoSelectedPatient(Object.assign(new PatientListDto(), {
      Id: this.selectedRegisterItem.PatientId,
      PatientNo: this.selectedRegisterItem.PatientNo,
      Kcstmr: this.selectedRegisterItem.PatientKcstmr,
      Name: this.selectedRegisterItem.PatientName,
      Birthday: this.selectedRegisterItem.PatientBirthday as any
    }), false);
  }

  private async loadPatientFromIdIfNeeded() {
    const id = Number(this.patientId || 0);
    await this.loadPatientInfoById(id, false);
  }

  private async refreshPatientInfoContent() {
    const id = Number(this.refreshPatientId || this.patientId || this.patientInfoSelectedPatient?.Id || 0);
    if (id) {
      await this.loadPatientInfoById(id, true);
      return;
    }

    this.loadPatientInfoSelectedDetail();
  }

  private async loadPatientInfoById(id: number, forceReload: boolean) {
    if (!id) {
      return;
    }

    if (!forceReload && Number(this.patientInfoSelectedPatient?.Id || 0) === id) {
      this.loadPatientInfoSelectedDetail();
      return;
    }

    try {
      const patient = await this.patientApi.Get(id);
      this.setPatientInfoSelectedPatient(this.mapPatientToListDto(patient), false, false);
      this.applyPatientInfoDetail(patient);
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    } catch (ex) {
      const errorMsg = this.handleErrorService.handleError(ex);
      if (errorMsg) {
        this.notification.showError(errorMsg);
      }
    }
  }

  private mapPatientToListDto(patient: any): PatientListDto {
    const phoneArea = patient?.PhoneArea || '';
    const phone = patient?.Phone || '';
    const phoneNo = patient?.PhoneHome || patient?.PhoneNo || (phone ? `${phoneArea}${phone}`.trim() : '');

    return Object.assign(new PatientListDto(), {
      Id: patient?.Id || patient?.id || this.patientId,
      PatientNo: patient?.PatientNo || '',
      Kcstmr: patient?.Kcstmr || '',
      Name: patient?.CName || patient?.Name || patient?.FullName || '',
      FullName: patient?.FullName || patient?.CName || patient?.Name || '',
      Birthday: patient?.Birthday,
      CId: patient?.CId || patient?.PatientCId || '',
      Sex: patient?.Sex ?? patient?.PatientSexCode,
      PhoneArea: phoneArea,
      Phone: phone,
      Mobile: patient?.CellPhone || patient?.Mobile || '',
      PhoneNo: phoneNo,
      PaySysA: patient?.PaySysA || '',
      DiscountCode: patient?.DiscountCode || '',
      DiscountId: patient?.DiscountId || 0,
      ICode: patient?.ICode || '',
      IsAboriginal: patient?.IsAboriginal ?? false,
      Family: patient?.Family || '',
      SpcIdentity: patient?.SpcIdentity || '',
      Handicapp: patient?.Handicapp || '',
      DrugAllergy: patient?.DrugAllergy || '',
      Remark: patient?.Remark || '',
      RaceCode: patient?.RaceCode || '',
      MajorInjuryTimes: patient?.MajorInjuryTimes || 0,
      MajorInjuryDate: patient?.MajorInjuryDate || null,
      MajorInjuryEndDate: patient?.MajorInjuryEndDate || null
    });
  }

  private setPatientInfoSelectedPatient(
    patient: PatientListDto,
    emit: boolean,
    loadDetail = true,
    source: PatientInfoSelectionSource = 'default'
  ) {
    this.patientInfoSelectedPatient = patient;
    this.petSelectionPending = true;
    this.petDetailPending = loadDetail && !!Number(patient?.Id || 0);
    this.petContext.begin('patient-info-window', Number(patient?.Id || 0));
    this.refreshPatientInfoAlertTags();
    this.collapsePatientInfoSearchResults();
    this.patientInfoFamilyMembers = [];
    this.clearPatientInfoRegisterInfo();
    this.applyPatientInfoSelectionSectionPolicy(source);
    this.savePatientInfoMemory();
    if (patient) {
      this.loadPatientInfoRegisterInfo(patient);
    }
    if (emit) {
      this.selectedPatientChange.emit(patient);
    }
    if (loadDetail) {
      this.loadPatientInfoSelectedDetail();
    }
    this.petSelectionPending = false;
    this.publishPetContext();
  }

  private async loadPatientInfoSelectedDetail() {
    const selectedPatient = this.patientInfoSelectedPatient;
    if (!selectedPatient) {
      this.patientInfoAlertTags = [];
      this.patientInfoFamilyMembers = [];
      this.clearPatientInfoRegisterInfo();
      return;
    }

    if (!this.patientInfoRegisterInfoLoading && !this.hasPatientInfoRegisterInfo) {
      this.loadPatientInfoRegisterInfo(selectedPatient);
    }

    this.loadPatientInfoSpecialIdentityOptions();

    const selectedIdentity = this.getPatientInfoIdentity(selectedPatient);
    const id = Number(selectedPatient.Id || 0);
    if (!id) {
      this.loadPatientInfoFamilyMembers(selectedPatient);
      return;
    }

    const requestId = ++this.patientInfoDetailRequestId;
    this.petDetailPending = true;
    try {
      const patient = await this.patientApi.Get(id);
      if (this.isDestroyed || requestId !== this.patientInfoDetailRequestId) {
        return;
      }

      if (selectedIdentity !== this.getPatientInfoIdentity(this.patientInfoSelectedPatient)) {
        return;
      }

      this.applyPatientInfoDetail(patient);
    } catch (ex) {
      // Detail enrichment is non-blocking; the panel keeps the list/search data already shown.
    } finally {
      if (!this.isDestroyed && requestId === this.patientInfoDetailRequestId
        && selectedIdentity === this.getPatientInfoIdentity(this.patientInfoSelectedPatient)) {
        this.petDetailPending = false;
        this.publishPetContext();
      }
    }
  }

  private applyPatientInfoDetail(patient: any) {
    const mappedPatient = this.mapPatientToListDto(patient);
    this.patientInfoSelectedPatient = Object.assign(
      new PatientListDto(),
      this.patientInfoSelectedPatient || {},
      mappedPatient
    );
    this.patientInfoDetailIdentity = this.getPatientInfoIdentity(this.patientInfoSelectedPatient);
    this.refreshPatientInfoAlertTags();
    this.savePatientInfoMemory();
    this.loadPatientInfoFamilyMembers(this.patientInfoSelectedPatient);
    if (!this.isDestroyed) {
      this.cdr.detectChanges();
    }
  }

  private async loadPatientInfoSpecialIdentityOptions() {
    if (this.patientInfoSpecialIdentityOptionsLoaded || this.patientInfoSpecialIdentityOptionsLoading) {
      return;
    }

    this.patientInfoSpecialIdentityOptionsLoading = true;
    try {
      const editOptions = await this.patientApi.GetEditOption();
      this.patientInfoSpecialIdentityOptions = editOptions?.spcIdentity || [];
      this.patientInfoSpecialIdentityOptionsLoaded = true;
      this.refreshPatientInfoAlertTags();
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    } catch (ex) {
      this.patientInfoSpecialIdentityOptionsLoaded = true;
    } finally {
      this.patientInfoSpecialIdentityOptionsLoading = false;
    }
  }

  private async loadPatientInfoRoomOptions() {
    if (this.patientInfoRoomOptionsLoaded) {
      return;
    }

    try {
      const codes = await this.clinicDataService.getSystemCodes(['C0120']);
      this.patientInfoRoomOptions = codes?.['C0120'] || [];
    } catch (ex) {
      this.patientInfoRoomOptions = [];
    } finally {
      this.patientInfoRoomOptionsLoaded = true;
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
    }
  }

  private async loadPatientInfoFamilyMembers(patient: PatientListDto) {
    const familyKey = this.getPatientInfoFamilyKey(patient);
    const requestId = ++this.patientInfoFamilyRequestId;
    this.patientInfoFamilyMembers = [];

    if (!familyKey) {
      this.patientInfoFamilyLoading = false;
      return;
    }

    this.patientInfoFamilyLoading = true;
    try {
      const query = this.createPatientInfoFamilyQuery(patient);
      const result = await this.patientApi.GetAll(query);
      if (this.isDestroyed || requestId !== this.patientInfoFamilyRequestId) {
        return;
      }

      this.patientInfoFamilyMembers = this.normalizePatientInfoRows(result?.Data?.data || [])
        .filter(member => !this.isSamePatientInfoPatient(member, patient));
    } catch (ex) {
      if (requestId === this.patientInfoFamilyRequestId) {
        this.patientInfoFamilyMembers = [];
      }
    } finally {
      if (requestId === this.patientInfoFamilyRequestId) {
        this.patientInfoFamilyLoading = false;
        if (!this.isDestroyed) {
          this.cdr.detectChanges();
        }
      }
    }
  }

  private clearPatientInfoRegisterInfo() {
    ++this.patientInfoRegisterInfoRequestId;
    this.patientInfoActiveTreatments = [];
    this.patientInfoPeriodItems = [];
    this.patientInfoReserveRecords = [];
    this.patientInfoClinicHistories = [];
    this.patientInfoRegisterInfoLoading = false;
    this.patientInfoClinicHistoryPage = 1;
  }

  private async loadPatientInfoRegisterInfo(patient: PatientListDto) {
    const patientId = Number(patient?.Id || 0);
    const requestId = ++this.patientInfoRegisterInfoRequestId;
    this.patientInfoActiveTreatments = [];
    this.patientInfoPeriodItems = [];
    this.patientInfoReserveRecords = [];
    this.patientInfoClinicHistories = [];
    this.patientInfoClinicHistoryPage = 1;

    if (!patientId) {
      this.patientInfoRegisterInfoLoading = false;
      return;
    }

    const hasRegisterItemInfo = this.isPatientInfoSelectedRegisterItem(patient)
      && (!!this.selectedRegisterItem?.PeriodItems?.length || !!this.selectedRegisterItem?.ClinicHistoryies?.length);

    if (hasRegisterItemInfo) {
      this.patientInfoPeriodItems = this.selectedRegisterItem?.PeriodItems || [];
      this.patientInfoClinicHistories = this.selectedRegisterItem?.ClinicHistoryies || [];
    }

    this.patientInfoRegisterInfoLoading = true;
    try {
      const [periodItems, clinicHistories, activeTreatments, reserveRecords] = await Promise.all([
        this.registerApi.GetRegisterPeriodItems(patientId),
        this.registerApi.GetRegisterClinicHistories(patientId),
        this.loadPatientInfoActiveDentalTreatments(patientId),
        this.loadPatientInfoReserveRecords(patientId)
      ]);

      if (this.isDestroyed || requestId !== this.patientInfoRegisterInfoRequestId) {
        return;
      }

      const resolvedClinicHistories = await this.resolvePatientInfoClinicHistoryIds(clinicHistories || []);
      if (this.isDestroyed || requestId !== this.patientInfoRegisterInfoRequestId) {
        return;
      }

      this.patientInfoActiveTreatments = activeTreatments;
      this.patientInfoPeriodItems = periodItems || [];
      this.patientInfoReserveRecords = reserveRecords || [];
      this.patientInfoClinicHistories = resolvedClinicHistories;
      this.patientInfoClinicHistoryPage = 1;
    } catch (ex) {
      if (requestId === this.patientInfoRegisterInfoRequestId && !hasRegisterItemInfo) {
        this.patientInfoActiveTreatments = [];
        this.patientInfoPeriodItems = [];
        this.patientInfoReserveRecords = [];
        this.patientInfoClinicHistories = [];
      }
    } finally {
      if (requestId === this.patientInfoRegisterInfoRequestId) {
        this.patientInfoRegisterInfoLoading = false;
        this.publishPetContext();
        if (!this.isDestroyed) {
          this.cdr.detectChanges();
        }
      }
    }
  }

  private isPatientInfoSelectedRegisterItem(patient: PatientListDto): boolean {
    const patientId = Number(patient?.Id || 0);
    return !!patientId && Number(this.selectedRegisterItem?.PatientId || 0) === patientId;
  }

  private async loadPatientInfoActiveDentalTreatments(patientId: number): Promise<MutiTreatmentListDto[]> {
    try {
      const regDate = this.getPatientInfoTreatmentQueryDate();
      const treatmentList = await this.registerService.GetEveryDentalTreatmentList(
        patientId,
        regDate,
        RegisterConst.SameTreat_牙科療程未定
      );
      return this.filterPatientInfoActiveDentalTreatments(treatmentList);
    } catch (ex) {
      return [];
    }
  }

  private async resolvePatientInfoClinicHistoryIds(rows: RegisterClinicHistoryDTO[]): Promise<RegisterClinicHistoryDTO[]> {
    const normalizedRows = (rows || []).map(row => {
      const item = Object.assign(new RegisterClinicHistoryDTO(), row);
      return item;
    });
    const pairs = normalizedRows
      .map(item => ({
        item,
        histId: this.getPatientInfoClinicHistoryHistId(item),
        regId: this.getPatientInfoClinicHistoryRegId(item)
      }))
      .filter(pair => !!pair.histId && !pair.regId);

    if (!pairs.length) {
      return normalizedRows;
    }

    try {
      const regIds = await this.histApi.GetRegIdList(pairs.map(pair => pair.histId));
      pairs.forEach((pair, index) => {
        const regId = Number(regIds?.[index] || 0);
        if (regId) {
          this.setPatientInfoClinicHistoryRegId(pair.item, regId);
        }
      });
    } catch (ex) {
      // Keep data-hist-id available; click-time histId lookup will retry without date inference.
    }

    return normalizedRows;
  }

  private async loadPatientInfoReserveRecords(patientId: number): Promise<ReserveItem[]> {
    try {
      const list = await this.reserveService.getPatientReserveList(patientId);
      return (list || [])
        .map(item => this.normalizePatientInfoReserveRecord(item))
        .filter(item => this.isVisiblePatientInfoReserveRecord(item))
        .sort((a, b) => this.getPatientInfoDateTime(a?.AppointmentDate) - this.getPatientInfoDateTime(b?.AppointmentDate));
    } catch (ex) {
      return [];
    }
  }

  private normalizePatientInfoReserveRecord(item: ReserveItem): ReserveItem {
    const appointmentDate = this.getPatientInfoValidDate(item?.AppointmentDate);
    return Object.assign(new ReserveItem(), item, {
      AppointmentDate: appointmentDate || item?.AppointmentDate,
      CheckInDate: appointmentDate ? DateHelper.getDate(appointmentDate) : item?.CheckInDate,
      CheckInTime: item?.CheckInTime || (appointmentDate ? DateHelper.getHourMinTime(appointmentDate, true) : ''),
      EstimatedTime: Number(item?.EstimatedTime || 0) || 15
    });
  }

  private isVisiblePatientInfoReserveRecord(item?: ReserveItem): boolean {
    if (!item || item.IsCheckedIn) {
      return false;
    }

    const statusCode = (item.StatusCode || '').toString().trim();
    if (statusCode && statusCode !== RegisterConst.Code_Status_Appointment) {
      return false;
    }

    const appointmentDate = this.getPatientInfoValidDate(item.AppointmentDate);
    if (!appointmentDate) {
      return false;
    }

    const now = new Date();
    if (appointmentDate.getTime() >= now.getTime()) {
      return true;
    }

    return this.isSamePatientInfoDate(appointmentDate, now);
  }

  private filterPatientInfoActiveDentalTreatments(treatmentList: MutiTreatmentListDto[]): MutiTreatmentListDto[] {
    return (treatmentList || [])
      .filter(item =>
        (item?.CusTP === RegisterConst.SameTreat_5_牙科同療 || item?.CusTP === RegisterConst.SameTreat_6_牙統療程)
        && (item as any)?.IsTheEnd !== true
        && !this.getPatientInfoDentalTreatmentDateEndValue(item)
      )
      .sort((a, b) => this.getPatientInfoDateTime(b?.StartDate) - this.getPatientInfoDateTime(a?.StartDate));
  }

  private getPatientInfoTreatmentQueryDate(): string {
    const rawDate = this.selectedRegisterItem?.RegTime
      || this.selectedRegisterItem?.RegDate
      || this.selectedRegisterItem?.ReserveDateTime
      || new Date();
    const date = this.getPatientInfoValidDate(rawDate) || new Date();
    return DateHelper.formatDate(date);
  }

  private getPatientInfoTreatmentHRxs(item?: MutiTreatmentListDto): any[] {
    const histList = item?.HistList || [];
    if (!histList.length) {
      return [];
    }

    return histList
      .map(hist => (hist as any)?.Hist?.HRxs || [])
      .reduce((acc: any[], hrxs: any[]) => acc.concat(hrxs || []), []);
  }

  private formatPatientInfoTreatmentIsqno(isqno: any, isqnoExt?: any): string {
    const no = (isqno || '').toString().trim();
    const ext = (isqnoExt || '').toString().trim();
    if (no && ext) {
      return `${no} (${ext})`;
    }
    return no || ext || '';
  }

  private getPatientInfoDentalTreatmentDateEndValue(item?: MutiTreatmentListDto): any {
    if (item?.DateEnd) {
      return item.DateEnd;
    }

    const histories = this.getPatientInfoDentalTreatmentHistories(item);
    const dateEndHist = histories.slice().reverse().find((hist: any) => !!hist?.Hist?.DateEnd);
    return dateEndHist?.Hist?.DateEnd || null;
  }

  private getPatientInfoDentalTreatmentLastVisitDate(item?: MutiTreatmentListDto): any {
    const histories = this.getPatientInfoDentalTreatmentHistories(item);
    return histories[histories.length - 1]?.Register?.RegDate || null;
  }

  private formatPatientInfoMinguoDate(value: any): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string' && /^\d{2,3}\/\d{1,2}\/\d{1,2}/.test(value.trim())) {
      return value.trim();
    }

    const date = this.getPatientInfoValidDate(value);
    if (!date) {
      return '';
    }

    const year = date.getFullYear() - 1911;
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  private getPatientInfoDateTime(value: any): number {
    return this.getPatientInfoValidDate(value)?.getTime() || 0;
  }

  private getPatientInfoValidDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const text = value.trim();
      const rocMatch = text.match(/^(\d{2,3})\/(\d{1,2})\/(\d{1,2})/);
      if (rocMatch) {
        const date = new Date(Number(rocMatch[1]) + 1911, Number(rocMatch[2]) - 1, Number(rocMatch[3]));
        return !isNaN(date.getTime()) ? date : null;
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    return !isNaN(date.getTime()) ? date : null;
  }

  private getPatientInfoReserveRecordStartTime(item?: ReserveItem): string {
    const appointmentDate = this.getPatientInfoValidDate(item?.AppointmentDate);
    return appointmentDate ? DateHelper.getHourMinTime(appointmentDate, true) : '';
  }

  private isSamePatientInfoDate(a: Date, b: Date): boolean {
    return !!a && !!b && a.toLocaleDateString() === b.toLocaleDateString();
  }

  // private scrollPatientInfoBodyToTop(behavior: ScrollBehavior = 'smooth') {
  //   setTimeout(() => {
  //     const body = document.querySelector('.patient-info-window .patient-info-body') as HTMLElement;
  //     if (!body) {
  //       return;
  //     }

  //     body.scrollTo({
  //       top: 0,
  //       behavior
  //     });
  //   });
  // }

  // private scrollPatientInfoCurrentIntoView() {
  //   setTimeout(() => {
  //     const body = document.querySelector('.patient-info-window .patient-info-body') as HTMLElement;
  //     const current = body?.querySelector('.patient-info-current') as HTMLElement;
  //     if (!body || !current) {
  //       return;
  //     }

  //     body.scrollTo({
  //       top: Math.max(current.offsetTop - body.offsetTop, 0),
  //       behavior: 'smooth'
  //     });
  //   });
  // }

  private scrollPatientInfo(target?: string | HTMLElement) {
    setTimeout(() => {
      const body = document.querySelector('.patient-info-window .patient-info-body') as HTMLElement;
      if (!body) return;

      if (typeof target === 'string') {
        target = document.querySelector(target) as HTMLElement;
      }
      if (!target) target = body;

      const targetTop = Math.max(target.offsetTop - body.offsetTop, 0);
      console.log('Scrolling patient info to', { targetTop, target, body });
      body.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    });
  }

  private createPatientInfoBaseQuery(): GetAllQuery {
    const query = new GetAllQuery();
    query.Birthday = '';
    query.PatientNo = '';
    query.Name = '';
    query.PhoneNo = '';
    query.Mobile = '';
    query.CId = '';
    query.Address = '';
    query.Family = '';
    query.SearchType = '';
    query.PatientId = 0;
    query.FuzzyQuery = '';
    query.FieldsToCheck = '';
    return query;
  }

  private createPatientInfoSearchQuery(keyword: string): GetAllQuery {
    const query = this.createPatientInfoBaseQuery();
    query.FuzzyQuery = (keyword || '').trim().toUpperCase();
    return query;
  }

  private createPatientInfoFamilyQuery(patient: PatientListDto): GetAllQuery {
    const query = this.createPatientInfoBaseQuery();
    const family = this.getPatientInfoValue(patient, 'Family');
    if (family) {
      query.Family = family;
      return query;
    }

    const mobile = this.getPatientInfoMobile(patient);
    if (mobile) {
      query.Mobile = mobile;
      return query;
    }

    const homePhone = this.getPatientInfoHomePhone(patient);
    if (homePhone) {
      query.PhoneNo = homePhone;
      return query;
    }

    query.FuzzyQuery = this.getPatientInfoFamilyKey(patient).trim().toUpperCase();
    return query;
  }

  private normalizePatientInfoRows(rows: PatientListDto[]): PatientListDto[] {
    const seen = new Set<string>();
    const normalizedRows: PatientListDto[] = [];

    (rows || []).forEach(row => {
      const patient = Object.assign(new PatientListDto(), row);
      const identity = this.getPatientInfoIdentity(patient);
      if (!identity || seen.has(identity)) {
        return;
      }

      seen.add(identity);
      normalizedRows.push(patient);
    });

    return normalizedRows;
  }

  private normalizePatientInfoSearchKeywordHistory(items: any[]): string[] {
    const rawItems = Array.isArray(items) ? items : [];
    const seen = new Set<string>();
    const keywords: string[] = [];

    rawItems.forEach(item => {
      const keyword = `${item || ''}`.trim();
      const normalized = this.normalizePatientInfoSearchKeyword(keyword);
      if (!normalized || seen.has(normalized)) {
        return;
      }

      seen.add(normalized);
      keywords.push(keyword);
    });

    return keywords.slice(0, this.patientInfoSearchKeywordHistoryLimit);
  }

  private rememberPatientInfoSearchKeyword(keyword: string) {
    const text = (keyword || '').trim();
    const normalized = this.normalizePatientInfoSearchKeyword(text);
    if (!normalized) {
      return;
    }

    this.patientInfoSearchKeywordHistory = [
      text,
      ...this.patientInfoSearchKeywordHistory.filter(item => this.normalizePatientInfoSearchKeyword(item) !== normalized)
    ].slice(0, this.patientInfoSearchKeywordHistoryLimit);
  }

  private updatePatientInfoSearchSuggestions() {
    const keyword = this.normalizePatientInfoSearchKeyword(this.patientInfoSearchKeyword);
    const suggestions = !keyword
      ? this.patientInfoSearchKeywordHistory.slice(0, this.patientInfoSearchSuggestionLimit)
      : this.patientInfoSearchKeywordHistory
        .map((value, index) => ({
          value,
          score: this.scorePatientInfoSearchKeyword(value, keyword, index)
        }))
        .filter(item => Number.isFinite(item.score))
        .sort((a, b) => b.score - a.score || a.value.localeCompare(b.value))
        .slice(0, this.patientInfoSearchSuggestionLimit)
        .map(item => item.value);

    this.patientInfoSearchSuggestions = suggestions;
  }

  private scorePatientInfoSearchKeyword(candidate: string, keyword: string, index: number): number {
    const candidateText = this.normalizePatientInfoSearchKeyword(candidate);
    if (!candidateText || !keyword) {
      return Number.NEGATIVE_INFINITY;
    }

    const matchIndex = candidateText.indexOf(keyword);
    if (matchIndex < 0) {
      return Number.NEGATIVE_INFINITY;
    }

    const prefixBonus = matchIndex === 0 ? 200 : 0;
    const lengthPenalty = Math.max(candidateText.length - keyword.length, 0);
    return 1000 - (matchIndex * 100) - lengthPenalty - index + prefixBonus;
  }

  private normalizePatientInfoSearchKeyword(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  private getPatientInfoValue(patient: any, key: string): string {
    const value = patient?.[key];
    if (value === null || value === undefined) {
      return '';
    }

    return `${value}`.trim();
  }

  private getPatientInfoSpcIdentityTexts(spcIdentity: string): string[] {
    const options = this.patientInfoSpecialIdentityOptions.length
      ? this.patientInfoSpecialIdentityOptions
      : this.patientInfoDefaultSpcIdentityOptions;

    if (!spcIdentity || !options.length) {
      return [];
    }

    return options
      .filter(option => {
        const index = Number(option.value);
        return Number.isFinite(index) && spcIdentity.charAt(index) === '1';
      })
      .map(option => (option.text || '').trim())
      .filter(text => !!text);
  }

  private isPatientInfoBitString(value: string): boolean {
    return /^[01]+$/.test((value || '').trim());
  }

  private getPatientInfoFamilyKey(patient: any): string {
    const family = this.getPatientInfoValue(patient, 'Family');
    if (family) {
      return family;
    }

    const phoneArea = this.getPatientInfoValue(patient, 'PhoneArea');
    const phone = this.getPatientInfoValue(patient, 'Phone');
    if (phone) {
      return `${phoneArea}${phone}`.trim();
    }

    return this.getPatientInfoValue(patient, 'PhoneNo') || this.getPatientInfoValue(patient, 'Mobile');
  }

  private isSamePatientInfoPatient(patientA?: PatientListDto, patientB?: PatientListDto): boolean {
    if (!patientA || !patientB) {
      return false;
    }

    if (patientA.Id && patientB.Id) {
      return Number(patientA.Id) === Number(patientB.Id);
    }

    if (patientA.PatientNo && patientB.PatientNo) {
      return patientA.PatientNo === patientB.PatientNo;
    }

    if (patientA.CId && patientB.CId) {
      return patientA.CId === patientB.CId;
    }

    return this.getPatientInfoIdentity(patientA) === this.getPatientInfoIdentity(patientB);
  }

  private getPatientInfoIdentity(patient?: PatientListDto): string {
    if (!patient) {
      return '';
    }

    if (patient.Id) {
      return `id:${patient.Id}`;
    }

    if (patient.PatientNo) {
      return `no:${patient.PatientNo}`;
    }

    return `name:${patient.Name || ''}|cid:${patient.CId || ''}`;
  }

  private loadPatientInfoMemory(): boolean {
    try {
      const memoryKey = this.getPatientInfoMemoryKey();
      const legacyMemoryKey = this.getLegacyPatientInfoMemoryKey();
      const raw = localStorage.getItem(memoryKey)
        || localStorage.getItem(legacyMemoryKey);
      if (!raw) {
        return false;
      }

      const memory = JSON.parse(raw);
      const shouldRewriteMemory = Object.prototype.hasOwnProperty.call(memory || {}, 'searchHistory')
        || !!localStorage.getItem(legacyMemoryKey);
      this.patientInfoSearchKeyword = memory?.searchKeyword || '';
      this.patientInfoSearchKeywordHistory = this.normalizePatientInfoSearchKeywordHistory(memory?.searchKeywordHistory);
      this.updatePatientInfoSearchSuggestions();
      this.patientInfoSelectedPatient = memory?.selectedPatient || null;
      this.patientInfoSimpleHistLayouts = this.normalizePatientInfoSimpleHistLayouts(memory?.simpleHistLayouts);
      if (memory?.windowState) {
        this.loadPatientInfoWindowState(memory.windowState);
        if (shouldRewriteMemory) {
          this.savePatientInfoMemory();
        }
        return true;
      }
      if (shouldRewriteMemory) {
        this.savePatientInfoMemory();
      }
      return false;
    } catch (ex) {
      this.patientInfoSearchKeywordHistory = [];
      this.patientInfoSelectedPatient = null;
      this.patientInfoSimpleHistLayouts = {};
      return false;
    }
  }

  private savePatientInfoMemory() {
    try {
      localStorage.setItem(this.getPatientInfoMemoryKey(), JSON.stringify({
        searchKeyword: this.patientInfoSearchKeyword,
        searchKeywordHistory: this.patientInfoSearchKeywordHistory,
        selectedPatient: this.patientInfoSelectedPatient,
        windowState: this.getPatientInfoWindowMemoryState(),
        simpleHistLayouts: this.patientInfoSimpleHistLayouts
      }));
      localStorage.removeItem(this.getLegacyPatientInfoMemoryKey());
    } catch (ex) {
      // localStorage quota or privacy settings should not block page workflow.
    }
  }

  private getPatientInfoMemoryKey(): string {
    const loginUser = UserCache.getLoginUser();
    const accountKey = loginUser?.UserId || loginUser?.CId || 'anonymous';
    return `${this.patientInfoMemoryKeyPrefix}-${this.memoryScope}-${UserCache.getLoginClinicId()}-${accountKey}`;
  }

  private getLegacyPatientInfoMemoryKey(): string {
    const loginUser = UserCache.getLoginUser();
    const accountKey = loginUser?.UserId || loginUser?.CId || 'anonymous';
    return `${this.legacyMemoryKeyPrefix}-${UserCache.getLoginClinicId()}-${accountKey}`;
  }

  private loadPatientInfoWindowState(state: any) {
    if (!state) {
      return;
    }

    const wasResizing = this.patientInfoWindow.resizing;
    this.patientInfoWindow.rect = this.getClampedPatientInfoRect(this.normalizePatientInfoRect(state.rect, this.patientInfoWindow.rect));
    this.patientInfoWindow.minimizedRect = this.getClampedPatientInfoMinimizedRect(this.normalizePatientInfoRect(state.minimizedRect, this.patientInfoWindow.minimizedRect));
    this.patientInfoWindow.floatingRectBeforePin = state.floatingRectBeforePin
      ? this.getClampedPatientInfoRect(this.normalizePatientInfoRect(state.floatingRectBeforePin, this.patientInfoWindow.rect))
      : null;
    this.patientInfoWindow.restoreState = state.restoreState
      ? {
        pinned: !!state.restoreState.pinned,
        pinSide: this.normalizePatientInfoPinSide(state.restoreState.pinSide),
        pinnedSize: this.getClampedPatientInfoPinnedSize(this.normalizePatientInfoPinSide(state.restoreState.pinSide), Number(state.restoreState.pinnedSize)),
        rect: this.getClampedPatientInfoRect(this.normalizePatientInfoRect(state.restoreState.rect, this.patientInfoWindow.rect))
      }
      : null;
    this.patientInfoWindow.pinSide = this.normalizePatientInfoPinSide(state.pinSide);
    this.patientInfoWindow.pinnedSize = this.getClampedPatientInfoPinnedSize(this.patientInfoWindow.pinSide, Number(state.pinnedSize));
    this.patientInfoWindow.pinned = !!state.pinned && !!this.patientInfoWindow.pinSide;
    this.patientInfoWindow.minimized = !!state.minimized;
    this.patientInfoWindow.dragging = false;
    this.patientInfoWindow.resizing = false;
    this.patientInfoWindow.resizeDir = '';
    this.patientInfoWindow.resizeHoverDir = '';
    if (wasResizing) {
      this.resizeStateChange.emit(false);
    }
  }

  private applyRoutePinnedLayout(): boolean {
    const side = this.normalizePatientInfoPinSide(this.route.snapshot.params['patientInfoPinSide']);
    const size = Number(this.route.snapshot.params['patientInfoPinSize']);
    if (!side || !Number.isFinite(size) || size <= 0) {
      return false;
    }

    const rect = this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    this.patientInfoWindow.rect = rect;
    this.patientInfoWindow.floatingRectBeforePin = this.patientInfoWindow.floatingRectBeforePin || { ...rect };
    this.patientInfoWindow.pinSide = side;
    this.patientInfoWindow.pinnedSize = this.getClampedPatientInfoPinnedSize(side, size);
    this.patientInfoWindow.pinned = true;
    this.patientInfoWindow.minimized = false;
    this.savePatientInfoMemory();
    return true;
  }

  private getPatientInfoWindowMemoryState() {
    return {
      rect: this.patientInfoWindow.rect,
      minimizedRect: this.patientInfoWindow.minimizedRect,
      floatingRectBeforePin: this.patientInfoWindow.floatingRectBeforePin,
      restoreState: this.patientInfoWindow.restoreState,
      pinned: this.patientInfoWindow.pinned,
      pinSide: this.patientInfoWindow.pinSide,
      pinnedSize: this.patientInfoWindow.pinnedSize,
      minimized: this.patientInfoWindow.minimized
    };
  }

  private normalizePatientInfoPinSide(side: any): '' | 'top' | 'right' | 'bottom' | 'left' {
    return side === 'top' || side === 'right' || side === 'bottom' || side === 'left' ? side : '';
  }

  private createPatientInfoSimpleHistLayoutState(maskVisible = true, restoreSavedLayout = maskVisible) {
    const rect = this.getPatientInfoSimpleHistCenteredRect();
    const initialState = {
      maskVisible,
      moved: false,
      dragging: false,
      resizing: false,
      resizeDir: '',
      resizeHoverDir: '',
      startX: 0,
      startY: 0,
      startOffsetX: 0,
      startOffsetY: 0,
      startRect: { ...rect },
      rect,
      pinned: false,
      pinSide: '' as '' | 'top' | 'right' | 'bottom' | 'left',
      pinnedSize: 0,
      floatingRectBeforePin: null
    };
    if (!restoreSavedLayout) {
      return initialState;
    }

    const savedLayout = this.patientInfoSimpleHistLayouts[this.getPatientInfoSimpleHistRouteKey()];
    if (!savedLayout) {
      return initialState;
    }

    const savedRect = this.getClampedPatientInfoSimpleHistRect(
      this.normalizePatientInfoRect(savedLayout.rect, rect)
    );
    const pinSide = this.normalizePatientInfoPinSide(savedLayout.pinSide);
    const pinned = !!savedLayout.pinned && !!pinSide;
    const floatingRectBeforePin = savedLayout.floatingRectBeforePin
      ? this.getClampedPatientInfoSimpleHistRect(
        this.normalizePatientInfoRect(savedLayout.floatingRectBeforePin, savedRect)
      )
      : null;

    return {
      ...initialState,
      maskVisible: false,
      moved: true,
      startRect: { ...savedRect },
      rect: savedRect,
      pinned,
      pinSide: pinned ? pinSide : '' as '' | 'top' | 'right' | 'bottom' | 'left',
      pinnedSize: pinned
        ? this.getClampedPatientInfoSimpleHistPinnedSize(pinSide, Number(savedLayout.pinnedSize))
        : 0,
      floatingRectBeforePin
    };
  }

  private normalizePatientInfoSimpleHistLayouts(value: any): { [routeKey: string]: PatientInfoSimpleHistMemoryLayout } {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.keys(value).reduce((layouts, routeKey) => {
      const layout = value[routeKey];
      if (!layout || typeof layout !== 'object' || !layout.rect) {
        return layouts;
      }
      layouts[routeKey] = {
        rect: layout.rect,
        pinned: !!layout.pinned,
        pinSide: this.normalizePatientInfoPinSide(layout.pinSide),
        pinnedSize: Number(layout.pinnedSize) || 0,
        floatingRectBeforePin: layout.floatingRectBeforePin || null
      };
      return layouts;
    }, {} as { [routeKey: string]: PatientInfoSimpleHistMemoryLayout });
  }

  private getPatientInfoSimpleHistRouteKey(): string {
    const currentUrl = (this.routeInfo.CurrentUrl || this.router.url || '')
      .split('?')[0]
      .split('#')[0];
    const companyCode = this.routeInfo.getCompanyCode();
    const segments = currentUrl.split('/').filter(segment => !!segment);
    if (companyCode && segments[0] === companyCode) {
      segments.shift();
    }
    return `/${segments.map(segment => segment.split(';')[0]).join('/')}`;
  }

  private savePatientInfoSimpleHistLayout(): void {
    if (!this.patientInfoSimpleHist.visible) {
      return;
    }

    const routeKey = this.getPatientInfoSimpleHistRouteKey();
    const visualRect = this.getPatientInfoSimpleHistVisualRect();
    const rect = this.patientInfoSimpleHist.pinned
      ? this.getClampedPatientInfoSimpleHistRect(this.patientInfoSimpleHist.rect)
      : this.getClampedPatientInfoSimpleHistRect(visualRect);
    this.patientInfoSimpleHistLayouts = {
      ...this.patientInfoSimpleHistLayouts,
      [routeKey]: {
        rect,
        pinned: this.patientInfoSimpleHist.pinned,
        pinSide: this.patientInfoSimpleHist.pinSide,
        pinnedSize: this.patientInfoSimpleHist.pinnedSize,
        floatingRectBeforePin: this.patientInfoSimpleHist.floatingRectBeforePin
          ? { ...this.patientInfoSimpleHist.floatingRectBeforePin }
          : null
      }
    };
    this.savePatientInfoMemory();
  }

  private getPatientInfoSimpleHistLayoutState() {
    return {
      maskVisible: this.patientInfoSimpleHist.maskVisible,
      moved: this.patientInfoSimpleHist.moved,
      dragging: this.patientInfoSimpleHist.dragging,
      resizing: this.patientInfoSimpleHist.resizing,
      resizeDir: this.patientInfoSimpleHist.resizeDir,
      resizeHoverDir: this.patientInfoSimpleHist.resizeHoverDir,
      startX: this.patientInfoSimpleHist.startX,
      startY: this.patientInfoSimpleHist.startY,
      startOffsetX: this.patientInfoSimpleHist.startOffsetX,
      startOffsetY: this.patientInfoSimpleHist.startOffsetY,
      startRect: { ...this.patientInfoSimpleHist.startRect },
      rect: { ...this.patientInfoSimpleHist.rect },
      pinned: this.patientInfoSimpleHist.pinned,
      pinSide: this.patientInfoSimpleHist.pinSide,
      pinnedSize: this.patientInfoSimpleHist.pinnedSize,
      floatingRectBeforePin: this.patientInfoSimpleHist.floatingRectBeforePin
        ? { ...this.patientInfoSimpleHist.floatingRectBeforePin }
        : null
    };
  }

  private getPatientInfoSimpleHistAvailableArea(): {
    left: number, top: number, right: number, bottom: number, width: number, height: number
  } {
    const viewport = this.getPatientInfoViewport();
    const area = {
      left: 0,
      top: this.getPatientInfoTopMenuOffset(viewport.height),
      right: viewport.width,
      bottom: viewport.height
    };
    if (this.patientInfoWindow.pinned && this.patientInfoWindow.pinSide) {
      const rect = this.patientInfoWindow.minimized
        ? this.getPatientInfoPinnedMinimizedRect(this.patientInfoWindow.pinSide)
        : this.getPatientInfoCurrentVisualRect();
      switch (this.patientInfoWindow.pinSide) {
        case 'left':
          area.left = Math.max(area.left, rect.left + rect.width);
          break;
        case 'right':
          area.right = Math.min(area.right, rect.left);
          break;
        case 'top':
          area.top = Math.max(area.top, rect.top + rect.height);
          break;
        case 'bottom':
          area.bottom = Math.min(area.bottom, rect.top);
          break;
      }
    }
    area.right = Math.max(area.left, area.right);
    area.bottom = Math.max(area.top, area.bottom);
    return {
      ...area,
      width: Math.max(0, area.right - area.left),
      height: Math.max(0, area.bottom - area.top)
    };
  }

  private getPatientInfoSimpleHistCenteredRect(width?: number, height?: number): PatientInfoSimpleHistRect {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    const rectWidth = this.clampPatientInfoSimpleHistSize(
      width || this.patientInfoSimpleHist.rect.width || this.patientInfoSimpleHistDefaultWidth,
      this.patientInfoSimpleHistMinWidth,
      area.width
    );
    const rectHeight = this.clampPatientInfoSimpleHistSize(
      height || this.patientInfoSimpleHist.rect.height || this.patientInfoSimpleHistDefaultHeight,
      this.patientInfoSimpleHistMinHeight,
      area.height
    );
    const centered = {
      left: area.left + Math.max(0, (area.width - rectWidth) / 2),
      top: area.top + Math.max(0, (area.height - rectHeight) / 2),
      width: rectWidth,
      height: rectHeight
    };
    const patientRect = !this.patientInfoWindow.minimized && !this.patientInfoWindow.pinned
      ? this.getPatientInfoCurrentVisualRect()
      : null;
    if (!patientRect || !this.patientInfoSimpleHistRectsIntersect(centered, patientRect)) {
      return centered;
    }

    const candidates = [
      { left: patientRect.left - rectWidth - 16, top: centered.top },
      { left: patientRect.left + patientRect.width + 16, top: centered.top },
      { left: centered.left, top: patientRect.top - rectHeight - 16 },
      { left: centered.left, top: patientRect.top + patientRect.height + 16 }
    ]
      .map(candidate => this.getClampedPatientInfoSimpleHistRect({ ...candidate, width: rectWidth, height: rectHeight }, area))
      .filter(candidate => !this.patientInfoSimpleHistRectsIntersect(candidate, patientRect));
    if (!candidates.length) {
      return centered;
    }

    return candidates.sort((a, b) =>
      this.getPatientInfoSimpleHistRectDistance(a, centered) - this.getPatientInfoSimpleHistRectDistance(b, centered)
    )[0];
  }

  private getPatientInfoSimpleHistVisualRect(): PatientInfoSimpleHistRect {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    if (this.patientInfoSimpleHist.pinned && this.patientInfoSimpleHist.pinSide) {
      const size = this.getClampedPatientInfoSimpleHistPinnedSize(
        this.patientInfoSimpleHist.pinSide,
        this.patientInfoSimpleHist.pinnedSize
      );
      switch (this.patientInfoSimpleHist.pinSide) {
        case 'left':
          return { left: area.left, top: area.top, width: size, height: area.height };
        case 'right':
          return { left: area.right - size, top: area.top, width: size, height: area.height };
        case 'top':
          return { left: area.left, top: area.top, width: area.width, height: size };
        case 'bottom':
          return { left: area.left, top: area.bottom - size, width: area.width, height: size };
      }
    }
    if (this.patientInfoSimpleHist.maskVisible && !this.patientInfoSimpleHist.moved && !this.patientInfoSimpleHist.resizing) {
      return this.getPatientInfoSimpleHistCenteredRect(
        this.patientInfoSimpleHist.rect.width,
        this.patientInfoSimpleHist.rect.height
      );
    }
    return this.getClampedPatientInfoSimpleHistRect(this.patientInfoSimpleHist.rect, area);
  }

  private getClampedPatientInfoSimpleHistRect(rect: PatientInfoSimpleHistRect, area = this.getPatientInfoSimpleHistAvailableArea()): PatientInfoSimpleHistRect {
    const minWidth = Math.min(this.patientInfoSimpleHistMinWidth, area.width);
    const minHeight = Math.min(this.patientInfoSimpleHistMinHeight, area.height);
    const width = this.clampPatientInfoSimpleHistSize(rect.width, minWidth, area.width);
    const height = this.clampPatientInfoSimpleHistSize(rect.height, minHeight, area.height);
    return {
      left: this.clampPatientInfoSimpleHistNumber(rect.left, area.left, Math.max(area.left, area.right - width)),
      top: this.clampPatientInfoSimpleHistNumber(rect.top, area.top, Math.max(area.top, area.bottom - height)),
      width,
      height
    };
  }

  private getClampedPatientInfoSimpleHistPinnedSize(side: '' | 'top' | 'right' | 'bottom' | 'left', size: number): number {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    if (side === 'left' || side === 'right') {
      return this.clampPatientInfoSimpleHistSize(size, Math.min(this.patientInfoSimpleHistMinWidth, area.width), area.width);
    }
    return this.clampPatientInfoSimpleHistSize(size, Math.min(this.patientInfoSimpleHistMinHeight, area.height), area.height);
  }

  private getClosestPatientInfoSimpleHistEdge(rect: PatientInfoSimpleHistRect): 'top' | 'right' | 'bottom' | 'left' {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    const distances = {
      top: Math.abs(rect.top - area.top),
      right: Math.abs(area.right - (rect.left + rect.width)),
      bottom: Math.abs(area.bottom - (rect.top + rect.height)),
      left: Math.abs(rect.left - area.left)
    };
    return (Object.keys(distances) as Array<'top' | 'right' | 'bottom' | 'left'>)
      .sort((a, b) => distances[a] - distances[b])[0];
  }

  private getPatientInfoSimpleHistDropPinSide(
    rect: PatientInfoSimpleHistRect,
    event: PointerEvent
  ): '' | 'top' | 'right' | 'bottom' | 'left' {
    const touchedSides = this.getTouchedPatientInfoSimpleHistEdges(rect);
    if (touchedSides.length === 0) {
      return '';
    }
    if (touchedSides.length === 1) {
      return touchedSides[0];
    }

    const dx = event.clientX - this.patientInfoSimpleHist.startX;
    const dy = event.clientY - this.patientInfoSimpleHist.startY;
    const vectorSide = this.getPatientInfoDragVectorSide(dx, dy);
    if (touchedSides.indexOf(vectorSide) >= 0) {
      return vectorSide;
    }

    const horizontalSide: 'left' | 'right' = dx < 0 ? 'left' : 'right';
    const verticalSide: 'top' | 'bottom' = dy < 0 ? 'top' : 'bottom';
    if (Math.abs(dx) >= Math.abs(dy)) {
      return touchedSides.indexOf(horizontalSide) >= 0 ? horizontalSide : touchedSides[0];
    }
    return touchedSides.indexOf(verticalSide) >= 0 ? verticalSide : touchedSides[0];
  }

  private getTouchedPatientInfoSimpleHistEdges(rect: PatientInfoSimpleHistRect): Array<'top' | 'right' | 'bottom' | 'left'> {
    const area = this.getPatientInfoSimpleHistAvailableArea();
    const sides: Array<'top' | 'right' | 'bottom' | 'left'> = [];
    if (rect.top <= area.top) {
      sides.push('top');
    }
    if (area.right - rect.left - rect.width <= 0) {
      sides.push('right');
    }
    if (area.bottom - rect.top - rect.height <= 0) {
      sides.push('bottom');
    }
    if (rect.left <= area.left) {
      sides.push('left');
    }
    return sides;
  }

  private pinPatientInfoSimpleHistToSide(
    side: 'top' | 'right' | 'bottom' | 'left',
    rect: PatientInfoSimpleHistRect
  ): void {
    const clampedRect = this.getClampedPatientInfoSimpleHistRect(rect);
    const size = side === 'left' || side === 'right' ? clampedRect.width : clampedRect.height;
    this.patientInfoSimpleHist.maskVisible = false;
    this.patientInfoSimpleHist.moved = true;
    this.patientInfoSimpleHist.rect = clampedRect;
    this.patientInfoSimpleHist.floatingRectBeforePin = { ...clampedRect };
    this.patientInfoSimpleHist.pinSide = side;
    this.patientInfoSimpleHist.pinnedSize = this.getClampedPatientInfoSimpleHistPinnedSize(side, size);
    this.patientInfoSimpleHist.pinned = true;
  }

  private isPatientInfoSimpleHistDragAwayFromPinnedEdge(dx: number, dy: number): boolean {
    switch (this.patientInfoSimpleHist.pinSide) {
      case 'left':
        return dx > this.patientInfoPinDetachDistance;
      case 'right':
        return dx < -this.patientInfoPinDetachDistance;
      case 'top':
        return dy > this.patientInfoPinDetachDistance;
      case 'bottom':
        return dy < -this.patientInfoPinDetachDistance;
      default:
        return false;
    }
  }

  private detachPinnedPatientInfoSimpleHistForDrag(event: PointerEvent): void {
    const side = this.patientInfoSimpleHist.pinSide;
    const area = this.getPatientInfoSimpleHistAvailableArea();
    const previousRect = this.patientInfoSimpleHist.floatingRectBeforePin || this.patientInfoSimpleHist.rect;
    let width = previousRect.width || this.patientInfoSimpleHistDefaultWidth;
    let height = previousRect.height || this.patientInfoSimpleHistDefaultHeight;
    if (side === 'left' || side === 'right') {
      width = this.patientInfoSimpleHist.pinnedSize;
    } else if (side === 'top' || side === 'bottom') {
      height = this.patientInfoSimpleHist.pinnedSize;
    }

    width = this.clampPatientInfoSimpleHistSize(width, this.patientInfoSimpleHistMinWidth, area.width);
    height = this.clampPatientInfoSimpleHistSize(height, this.patientInfoSimpleHistMinHeight, area.height);
    const offsetX = this.clampPatientInfoSimpleHistNumber(
      this.patientInfoSimpleHist.startOffsetX,
      Math.min(24, width),
      Math.max(Math.min(24, width), width - 24)
    );
    const offsetY = this.clampPatientInfoSimpleHistNumber(
      this.patientInfoSimpleHist.startOffsetY,
      Math.min(24, height),
      Math.max(Math.min(24, height), height - 24)
    );
    const rect = this.getClampedPatientInfoSimpleHistRect({
      left: event.clientX - offsetX,
      top: event.clientY - offsetY,
      width,
      height
    }, area);

    this.patientInfoSimpleHist.rect = rect;
    this.patientInfoSimpleHist.floatingRectBeforePin = { ...rect };
    this.patientInfoSimpleHist.pinned = false;
    this.patientInfoSimpleHist.pinSide = '';
    this.patientInfoSimpleHist.pinnedSize = 0;
    this.patientInfoSimpleHist.startRect = rect;
    this.patientInfoSimpleHist.startX = event.clientX;
    this.patientInfoSimpleHist.startY = event.clientY;
    this.patientInfoSimpleHist.startOffsetX = offsetX;
    this.patientInfoSimpleHist.startOffsetY = offsetY;
  }

  private clampPatientInfoSimpleHistNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(Number.isFinite(value) ? value : min, min), max);
  }

  private clampPatientInfoSimpleHistSize(value: number, min: number, max: number): number {
    if (max <= 0) {
      return 0;
    }
    return this.clampPatientInfoSimpleHistNumber(value, Math.min(min, max), max);
  }

  private patientInfoSimpleHistRectsIntersect(a: PatientInfoSimpleHistRect, b: PatientInfoSimpleHistRect): boolean {
    return a.left < b.left + b.width
      && a.left + a.width > b.left
      && a.top < b.top + b.height
      && a.top + a.height > b.top;
  }

  private getPatientInfoSimpleHistRectDistance(a: PatientInfoSimpleHistRect, b: PatientInfoSimpleHistRect): number {
    return Math.abs(a.left - b.left) + Math.abs(a.top - b.top);
  }

  private normalizePatientInfoRect(rect: any, fallback: { left: number, top: number, width: number, height: number }) {
    return {
      left: Number.isFinite(Number(rect?.left)) ? Number(rect.left) : fallback.left,
      top: Number.isFinite(Number(rect?.top)) ? Number(rect.top) : fallback.top,
      width: Number.isFinite(Number(rect?.width)) ? Number(rect.width) : fallback.width,
      height: Number.isFinite(Number(rect?.height)) ? Number(rect.height) : fallback.height
    };
  }

  private patientInfoSimpleHistDocumentPointerMove = (event: PointerEvent) => {
    if (this.patientInfoSimpleHist.dragging) {
      const dx = event.clientX - this.patientInfoSimpleHist.startX;
      const dy = event.clientY - this.patientInfoSimpleHist.startY;
      if (this.patientInfoSimpleHist.pinned) {
        if (this.isPatientInfoSimpleHistDragAwayFromPinnedEdge(dx, dy)) {
          this.patientInfoSimpleHist.maskVisible = false;
          this.patientInfoSimpleHist.moved = true;
          this.detachPinnedPatientInfoSimpleHistForDrag(event);
        }
        return;
      }

      if (Math.abs(dx) > this.patientInfoSimpleHistDragThreshold || Math.abs(dy) > this.patientInfoSimpleHistDragThreshold) {
        this.patientInfoSimpleHist.moved = true;
        this.patientInfoSimpleHist.maskVisible = false;
      }

      if (this.patientInfoSimpleHist.moved) {
        const start = this.patientInfoSimpleHist.startRect;
        this.patientInfoSimpleHist.rect = this.getClampedPatientInfoSimpleHistRect({
          left: start.left + dx,
          top: start.top + dy,
          width: start.width,
          height: start.height
        });
      }
      return;
    }

    if (!this.patientInfoSimpleHist.resizing) {
      return;
    }

    if (this.patientInfoSimpleHist.pinned && this.patientInfoSimpleHist.pinSide) {
      const delta = this.patientInfoSimpleHist.pinSide === 'left' || this.patientInfoSimpleHist.pinSide === 'right'
        ? event.clientX - this.patientInfoSimpleHist.startX
        : event.clientY - this.patientInfoSimpleHist.startY;
      const signedDelta = this.patientInfoSimpleHist.pinSide === 'right' || this.patientInfoSimpleHist.pinSide === 'bottom'
        ? -delta
        : delta;
      this.patientInfoSimpleHist.pinnedSize = this.getClampedPatientInfoSimpleHistPinnedSize(
        this.patientInfoSimpleHist.pinSide,
        this.patientInfoSimpleHist.pinnedSize + signedDelta
      );
      this.patientInfoSimpleHist.startX = event.clientX;
      this.patientInfoSimpleHist.startY = event.clientY;
      return;
    }

    const dx = event.clientX - this.patientInfoSimpleHist.startX;
    const dy = event.clientY - this.patientInfoSimpleHist.startY;
    const start = this.patientInfoSimpleHist.startRect;
    const area = this.getPatientInfoSimpleHistAvailableArea();
    let left = start.left;
    let top = start.top;
    let width = start.width;
    let height = start.height;
    const direction = this.patientInfoSimpleHist.resizeDir;

    if (direction.indexOf('e') >= 0) {
      width = this.clampPatientInfoSimpleHistSize(start.width + dx, this.patientInfoSimpleHistMinWidth, area.right - start.left);
    }
    if (direction.indexOf('s') >= 0) {
      height = this.clampPatientInfoSimpleHistSize(start.height + dy, this.patientInfoSimpleHistMinHeight, area.bottom - start.top);
    }
    if (direction.indexOf('w') >= 0) {
      const maxLeft = start.left + start.width - Math.min(this.patientInfoSimpleHistMinWidth, area.width);
      left = this.clampPatientInfoSimpleHistNumber(start.left + dx, area.left, Math.max(area.left, maxLeft));
      width = start.width + start.left - left;
    }
    if (direction.indexOf('n') >= 0) {
      const maxTop = start.top + start.height - Math.min(this.patientInfoSimpleHistMinHeight, area.height);
      top = this.clampPatientInfoSimpleHistNumber(start.top + dy, area.top, Math.max(area.top, maxTop));
      height = start.height + start.top - top;
    }

    this.patientInfoSimpleHist.rect = this.getClampedPatientInfoSimpleHistRect({ left, top, width, height }, area);
  };

  private patientInfoSimpleHistDocumentPointerUp = (event: PointerEvent) => {
    const wasDragging = this.patientInfoSimpleHist.dragging;
    const wasResizing = this.patientInfoSimpleHist.resizing;
    const wasFloatingDrag = wasDragging && this.patientInfoSimpleHist.moved && !this.patientInfoSimpleHist.pinned;
    const shouldCenterAfterResize = wasResizing
      && this.patientInfoSimpleHist.maskVisible
      && !this.patientInfoSimpleHist.moved
      && !this.patientInfoSimpleHist.pinned;

    this.patientInfoSimpleHist.dragging = false;
    this.patientInfoSimpleHist.resizing = false;
    this.patientInfoSimpleHist.resizeDir = '';
    if (shouldCenterAfterResize) {
      this.patientInfoSimpleHist.rect = this.getPatientInfoSimpleHistCenteredRect(
        this.patientInfoSimpleHist.rect.width,
        this.patientInfoSimpleHist.rect.height
      );
    }
    if (wasResizing) {
      this.syncPatientInfoSimpleHistResizeHoverAfterPointer(event);
    }
    if (wasFloatingDrag && event.type !== 'pointercancel') {
      const touchedSide = this.getPatientInfoSimpleHistDropPinSide(this.patientInfoSimpleHist.rect, event);
      if (touchedSide) {
        this.pinPatientInfoSimpleHistToSide(touchedSide, this.patientInfoSimpleHist.rect);
      }
    }
    this.removePatientInfoSimpleHistDocumentListeners();
    if (wasResizing || (wasDragging && this.patientInfoSimpleHist.moved)) {
      this.savePatientInfoSimpleHistLayout();
    }
  };

  private addPatientInfoSimpleHistDocumentListeners() {
    document.addEventListener('pointermove', this.patientInfoSimpleHistDocumentPointerMove);
    document.addEventListener('pointerup', this.patientInfoSimpleHistDocumentPointerUp);
    document.addEventListener('pointercancel', this.patientInfoSimpleHistDocumentPointerUp);
  }

  private removePatientInfoSimpleHistDocumentListeners() {
    document.removeEventListener('pointermove', this.patientInfoSimpleHistDocumentPointerMove);
    document.removeEventListener('pointerup', this.patientInfoSimpleHistDocumentPointerUp);
    document.removeEventListener('pointercancel', this.patientInfoSimpleHistDocumentPointerUp);
  }

  private syncPatientInfoSimpleHistResizeHoverAfterPointer(event: PointerEvent) {
    if (event.type === 'pointercancel') {
      this.patientInfoSimpleHist.resizeHoverDir = '';
      return;
    }
    const element = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement;
    const handle = element?.closest('.patient-info-simple-hist-resize-handle') as HTMLElement;
    if (!handle) {
      this.patientInfoSimpleHist.resizeHoverDir = '';
      return;
    }
    this.patientInfoSimpleHist.resizeHoverDir = this.patientInfoFloatingResizeHandles
      .slice()
      .sort((a, b) => b.length - a.length)
      .find(direction => handle.classList.contains(direction)) || '';
  }

  private patientInfoDocumentPointerMove = (event: PointerEvent) => {
    if (this.patientInfoWindow.dragging) {
      const dx = event.clientX - this.patientInfoWindow.startX;
      const dy = event.clientY - this.patientInfoWindow.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.patientInfoWindow.moved = true;
      }

      if (this.patientInfoWindow.minimized) {
        const rect = this.patientInfoWindow.startRect;
        this.patientInfoWindow.minimizedRect = this.getClampedPatientInfoMinimizedRect({
          left: rect.left + dx,
          top: rect.top + dy,
          width: this.patientInfoMinimizedSize,
          height: this.patientInfoMinimizedSize
        });
      } else if (this.patientInfoWindow.pinned) {
        if (this.isPatientInfoDragAwayFromPinnedEdge(dx, dy)) {
          this.detachPinnedPatientInfoWindowForDrag(event);
        }
      } else {
        const rect = this.patientInfoWindow.startRect;
        const nextRect = this.getClampedPatientInfoRect({
          ...rect,
          left: rect.left + dx,
          top: rect.top + dy
        });
        this.patientInfoWindow.rect = nextRect;
      }
      return;
    }

    if (this.patientInfoWindow.resizing) {
      if (this.patientInfoWindow.pinned) {
        this.resizePinnedPatientInfoWindow(event);
      } else {
        this.resizeFloatingPatientInfoWindow(event);
      }
      this.emitLayoutChange();
    }
  };

  private patientInfoDocumentPointerUp = (event: PointerEvent) => {
    const wasDragging = this.patientInfoWindow.dragging;
    const wasResizing = this.patientInfoWindow.resizing;
    const wasFloatingDrag = wasDragging && this.patientInfoWindow.moved && !this.patientInfoWindow.minimized && !this.patientInfoWindow.pinned;
    const shouldRestoreMinimized = this.patientInfoWindow.minimized && wasDragging && !this.patientInfoWindow.moved;

    this.patientInfoWindow.dragging = false;
    this.patientInfoWindow.resizing = false;
    this.patientInfoWindow.resizeDir = '';
    if (wasResizing) {
      this.syncPatientInfoResizeHoverAfterPointer(event);
      this.resizeStateChange.emit(false);
    }

    if (shouldRestoreMinimized) {
      this.restorePatientInfoWindow();
      this.removePatientInfoDocumentListeners();
      this.savePatientInfoMemory();
      return;
    }

    if (wasFloatingDrag) {
      const touchedSide = this.getPatientInfoDropPinSide(this.patientInfoWindow.rect, event);
      if (touchedSide) {
        this.pinPatientInfoWindowToSide(touchedSide, this.patientInfoWindow.rect);
      }
    }

    this.removePatientInfoDocumentListeners();
    if (wasDragging || wasResizing) {
      this.savePatientInfoMemory();
      this.emitLayoutChange();
    }
  };

  private addPatientInfoDocumentListeners() {
    document.addEventListener('pointermove', this.patientInfoDocumentPointerMove);
    document.addEventListener('pointerup', this.patientInfoDocumentPointerUp);
    document.addEventListener('pointercancel', this.patientInfoDocumentPointerUp);
  }

  private removePatientInfoDocumentListeners() {
    document.removeEventListener('pointermove', this.patientInfoDocumentPointerMove);
    document.removeEventListener('pointerup', this.patientInfoDocumentPointerUp);
    document.removeEventListener('pointercancel', this.patientInfoDocumentPointerUp);
  }

  private syncPatientInfoResizeHoverAfterPointer(event: PointerEvent) {
    if (event.type === 'pointercancel') {
      this.patientInfoWindow.resizeHoverDir = '';
      return;
    }

    const element = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement;
    const handle = element?.closest('.patient-info-resize-handle') as HTMLElement;
    if (!handle) {
      this.patientInfoWindow.resizeHoverDir = '';
      return;
    }

    const direction = this.patientInfoFloatingResizeHandles
      .slice()
      .sort((a, b) => b.length - a.length)
      .find(dir => handle.classList.contains(dir));
    this.patientInfoWindow.resizeHoverDir = direction || '';
  }

  private restorePatientInfoWindow() {
    const restoreState = this.patientInfoWindow.restoreState;
    if (restoreState) {
      this.patientInfoWindow.pinned = restoreState.pinned;
      this.patientInfoWindow.pinSide = restoreState.pinSide;
      this.patientInfoWindow.pinnedSize = restoreState.pinnedSize;
      this.patientInfoWindow.rect = this.getClampedPatientInfoRect(restoreState.rect);
    }
    this.patientInfoWindow.minimized = false;
    this.savePatientInfoMemory();
    this.emitLayoutChange();
  }

  private unpinPatientInfoWindow() {
    const side = this.patientInfoWindow.pinSide;
    const rect = this.patientInfoWindow.floatingRectBeforePin
      ? { ...this.patientInfoWindow.floatingRectBeforePin }
      : { ...this.patientInfoWindow.rect };

    if (side === 'left' || side === 'right') {
      rect.width = this.patientInfoWindow.pinnedSize;
    } else if (side === 'top' || side === 'bottom') {
      rect.height = this.patientInfoWindow.pinnedSize;
    }

    this.patientInfoWindow.rect = this.getClampedPatientInfoRect(rect);
    this.patientInfoWindow.pinned = false;
    this.patientInfoWindow.pinSide = '';
    this.savePatientInfoMemory();
    this.emitLayoutChange();
  }

  private pinPatientInfoWindowToSide(side: 'top' | 'right' | 'bottom' | 'left', rect: { left: number, top: number, width: number, height: number }) {
    const clampedRect = this.getClampedPatientInfoRect(rect);
    const size = side === 'left' || side === 'right' ? clampedRect.width : clampedRect.height;

    this.patientInfoWindow.rect = clampedRect;
    this.patientInfoWindow.floatingRectBeforePin = { ...clampedRect };
    this.patientInfoWindow.pinSide = side;
    this.patientInfoWindow.pinnedSize = this.getClampedPatientInfoPinnedSize(side, size);
    this.patientInfoWindow.pinned = true;
    this.savePatientInfoMemory();
    this.emitLayoutChange();
  }

  private isPatientInfoDragAwayFromPinnedEdge(dx: number, dy: number): boolean {
    switch (this.patientInfoWindow.startPinSide || this.patientInfoWindow.pinSide) {
      case 'left':
        return dx > this.patientInfoPinDetachDistance;
      case 'right':
        return dx < -this.patientInfoPinDetachDistance;
      case 'top':
        return dy > this.patientInfoPinDetachDistance;
      case 'bottom':
        return dy < -this.patientInfoPinDetachDistance;
      default:
        return false;
    }
  }

  private detachPinnedPatientInfoWindowForDrag(event: PointerEvent) {
    const side = this.patientInfoWindow.startPinSide || this.patientInfoWindow.pinSide;
    const rect = this.getPatientInfoDetachedRect(side, event);

    this.patientInfoWindow.rect = rect;
    this.patientInfoWindow.floatingRectBeforePin = { ...rect };
    this.patientInfoWindow.pinned = false;
    this.patientInfoWindow.pinSide = '';
    this.patientInfoWindow.startRect = rect;
    this.patientInfoWindow.startX = event.clientX;
    this.patientInfoWindow.startY = event.clientY;
    this.emitLayoutChange();
  }

  private getPatientInfoDetachedRect(side: '' | 'top' | 'right' | 'bottom' | 'left', event: PointerEvent) {
    const area = this.getPatientInfoAvailableArea();
    const previousRect = this.patientInfoWindow.floatingRectBeforePin || this.patientInfoWindow.rect || {
      left: 32,
      top: 136,
      width: 360,
      height: 320
    };
    let width = previousRect.width || 360;
    let height = previousRect.height || 320;

    if (side === 'left' || side === 'right') {
      width = this.patientInfoWindow.pinnedSize;
    } else if (side === 'top' || side === 'bottom') {
      height = this.patientInfoWindow.pinnedSize;
    }

    width = this.clamp(width, this.patientInfoMinWidth, Math.max(this.patientInfoMinWidth, area.width));
    height = this.clamp(height, this.patientInfoMinHeight, Math.max(this.patientInfoMinHeight, area.height));
    const offsetX = this.clamp(this.patientInfoWindow.startOffsetX, 24, Math.max(24, width - 24));
    const offsetY = this.clamp(this.patientInfoWindow.startOffsetY, 24, Math.max(24, height - 24));

    return this.getClampedPatientInfoRect({
      left: event.clientX - offsetX,
      top: event.clientY - offsetY,
      width,
      height
    });
  }

  private resizePinnedPatientInfoWindow(event: PointerEvent) {
    const dx = event.clientX - this.patientInfoWindow.startX;
    const dy = event.clientY - this.patientInfoWindow.startY;
    let size = this.patientInfoWindow.startPinnedSize;

    switch (this.patientInfoWindow.pinSide) {
      case 'left':
        size += dx;
        break;
      case 'right':
        size -= dx;
        break;
      case 'top':
        size += dy;
        break;
      case 'bottom':
        size -= dy;
        break;
    }

    this.patientInfoWindow.pinnedSize = this.getClampedPatientInfoPinnedSize(this.patientInfoWindow.pinSide, size);
  }

  private resizeFloatingPatientInfoWindow(event: PointerEvent) {
    const dx = event.clientX - this.patientInfoWindow.startX;
    const dy = event.clientY - this.patientInfoWindow.startY;
    const area = this.getPatientInfoAvailableArea();
    const start = this.patientInfoWindow.startRect;
    let left = start.left;
    let top = start.top;
    let width = start.width;
    let height = start.height;
    const dir = this.patientInfoWindow.resizeDir;

    if (dir.indexOf('e') >= 0) {
      width = this.clamp(start.width + dx, this.patientInfoMinWidth, area.width - start.left);
    }

    if (dir.indexOf('s') >= 0) {
      height = this.clamp(start.height + dy, this.patientInfoMinHeight, Math.max(this.patientInfoMinHeight, area.bottom - start.top));
    }

    if (dir.indexOf('w') >= 0) {
      const maxLeft = start.left + start.width - this.patientInfoMinWidth;
      left = this.clamp(start.left + dx, 0, maxLeft);
      width = start.width + (start.left - left);
    }

    if (dir.indexOf('n') >= 0) {
      const maxTop = start.top + start.height - this.patientInfoMinHeight;
      top = this.clamp(start.top + dy, area.top, Math.max(area.top, maxTop));
      height = start.height + (start.top - top);
    }

    this.patientInfoWindow.rect = this.getClampedPatientInfoRect({ left, top, width, height });
  }

  private getPatientInfoResizeShadowStyle(): { [key: string]: string } {
    const direction = this.patientInfoWindow.resizing
      ? this.patientInfoWindow.resizeDir
      : this.patientInfoWindow.resizeHoverDir;
    if (!direction) {
      return {};
    }

    const offset = this.getPatientInfoResizeShadowOffset(direction);
    return {
      'box-shadow': `inset 0 1px 0 rgba(255, 255, 255, 0.82), inset 0 -1px 0 rgba(255, 255, 255, 0.28), 0 0 0 1px var(--main-color), ${offset.x/4}px ${offset.y/4}px 16px var(--main-color)`
    };
  }

  private getPatientInfoResizeShadowOffset(direction: string): { x: number, y: number } {
    const x = direction.indexOf('w') >= 0 ? -18 : direction.indexOf('e') >= 0 ? 18 : 0;
    const y = direction.indexOf('n') >= 0 ? -18 : direction.indexOf('s') >= 0 ? 18 : 0;
    return { x, y };
  }

  private getPatientInfoCurrentVisualRect(): { left: number, top: number, width: number, height: number } {
    const area = this.getPatientInfoAvailableArea();

    if (!this.patientInfoWindow.pinned) {
      return this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    }

    const size = this.getClampedPatientInfoPinnedSize(this.patientInfoWindow.pinSide, this.patientInfoWindow.pinnedSize);
    switch (this.patientInfoWindow.pinSide) {
      case 'left':
        return { left: 0, top: area.top, width: size, height: area.height };
      case 'right':
        return { left: area.width - size, top: area.top, width: size, height: area.height };
      case 'top':
        return { left: 0, top: area.top, width: area.width, height: size };
      case 'bottom':
        return { left: 0, top: area.bottom - size, width: area.width, height: size };
      default:
        return this.getClampedPatientInfoRect(this.patientInfoWindow.rect);
    }
  }

  private getPatientInfoMinimizedTargetRect(
    visualRect: { left: number, top: number, width: number, height: number },
    side: '' | 'top' | 'right' | 'bottom' | 'left'
  ) {
    const area = this.getPatientInfoAvailableArea();
    let left = visualRect.left + visualRect.width - this.patientInfoMinimizedSize;
    let top = visualRect.top;

    if (side === 'left') {
      left = 0;
      top = visualRect.top;
    } else if (side === 'bottom') {
      left = area.width - this.patientInfoMinimizedSize;
      top = area.bottom - this.patientInfoMinimizedSize;
    }

    return this.getClampedPatientInfoMinimizedRect({
      left,
      top,
      width: this.patientInfoMinimizedSize,
      height: this.patientInfoMinimizedSize
    });
  }

  private getPatientInfoPinnedMinimizedRect(side: 'top' | 'right' | 'bottom' | 'left') {
    const area = this.getPatientInfoAvailableArea();
    const verticalThickness = this.clamp(this.patientInfoPinnedMinimizedThickness, 1, Math.max(1, area.width));
    const horizontalThickness = this.clamp(this.patientInfoPinnedMinimizedThickness, 1, Math.max(1, area.height));

    switch (side) {
      case 'left':
        return { left: 0, top: area.top, width: verticalThickness, height: area.height };
      case 'right':
        return { left: area.width - verticalThickness, top: area.top, width: verticalThickness, height: area.height };
      case 'top':
        return { left: 0, top: area.top, width: area.width, height: horizontalThickness };
      case 'bottom':
        return { left: 0, top: area.bottom - horizontalThickness, width: area.width, height: horizontalThickness };
    }

    return this.getClampedPatientInfoMinimizedRect(this.patientInfoWindow.minimizedRect);
  }

  private getClosestPatientInfoEdge(rect: { left: number, top: number, width: number, height: number }): 'top' | 'right' | 'bottom' | 'left' {
    const area = this.getPatientInfoAvailableArea();
    const distances = [
      { side: 'top' as 'top', value: rect.top - area.top },
      { side: 'right' as 'right', value: area.width - rect.left - rect.width },
      { side: 'bottom' as 'bottom', value: area.bottom - rect.top - rect.height },
      { side: 'left' as 'left', value: rect.left }
    ];
    distances.sort((a, b) => a.value - b.value);
    return distances[0].side;
  }

  private getPatientInfoDropPinSide(
    rect: { left: number, top: number, width: number, height: number },
    event: PointerEvent
  ): '' | 'top' | 'right' | 'bottom' | 'left' {
    const touchedSides = this.getTouchedPatientInfoEdges(rect);
    if (touchedSides.length === 0) {
      return '';
    }

    if (touchedSides.length === 1) {
      return touchedSides[0];
    }

    const vectorSide = this.getPatientInfoDragVectorSide(
      event.clientX - this.patientInfoWindow.startX,
      event.clientY - this.patientInfoWindow.startY
    );

    if (touchedSides.indexOf(vectorSide) >= 0) {
      return vectorSide;
    }

    const horizontalSide: 'left' | 'right' = event.clientX - this.patientInfoWindow.startX < 0 ? 'left' : 'right';
    const verticalSide: 'top' | 'bottom' = event.clientY - this.patientInfoWindow.startY < 0 ? 'top' : 'bottom';
    if (Math.abs(event.clientX - this.patientInfoWindow.startX) >= Math.abs(event.clientY - this.patientInfoWindow.startY)) {
      return touchedSides.indexOf(horizontalSide) >= 0 ? horizontalSide : touchedSides[0];
    }
    return touchedSides.indexOf(verticalSide) >= 0 ? verticalSide : touchedSides[0];
  }

  private getPatientInfoDragVectorSide(dx: number, dy: number): 'top' | 'right' | 'bottom' | 'left' {
    if (Math.abs(dx) >= Math.abs(dy)) {
      return dx < 0 ? 'left' : 'right';
    }

    return dy < 0 ? 'top' : 'bottom';
  }

  private getTouchedPatientInfoEdges(rect: { left: number, top: number, width: number, height: number }): Array<'top' | 'right' | 'bottom' | 'left'> {
    const area = this.getPatientInfoAvailableArea();
    const sides: Array<'top' | 'right' | 'bottom' | 'left'> = [];

    if (rect.top <= area.top) {
      sides.push('top');
    }
    if (area.width - rect.left - rect.width <= 0) {
      sides.push('right');
    }
    if (area.bottom - rect.top - rect.height <= 0) {
      sides.push('bottom');
    }
    if (rect.left <= 0) {
      sides.push('left');
    }

    return sides;
  }

  private getClampedPatientInfoRect(rect: { left: number, top: number, width: number, height: number }) {
    const area = this.getPatientInfoAvailableArea();
    const width = this.clamp(rect.width, this.patientInfoMinWidth, Math.max(this.patientInfoMinWidth, area.width));
    const height = this.clamp(rect.height, this.patientInfoMinHeight, Math.max(this.patientInfoMinHeight, area.height));
    const left = this.clamp(rect.left, 0, Math.max(0, area.width - width));
    const top = this.clamp(rect.top, area.top, Math.max(area.top, area.bottom - height));
    return { left, top, width, height };
  }

  private getClampedPatientInfoMinimizedRect(rect: { left: number, top: number, width: number, height: number }) {
    const area = this.getPatientInfoAvailableArea();
    return {
      left: this.clamp(rect.left, 0, Math.max(0, area.width - this.patientInfoMinimizedSize)),
      top: this.clamp(rect.top, area.top, Math.max(area.top, area.bottom - this.patientInfoMinimizedSize)),
      width: this.patientInfoMinimizedSize,
      height: this.patientInfoMinimizedSize
    };
  }

  private getClampedPatientInfoPinnedSize(side: '' | 'top' | 'right' | 'bottom' | 'left', size: number): number {
    const area = this.getPatientInfoAvailableArea();
    const max = side === 'left' || side === 'right' ? area.width * 0.5 : area.height * 0.5;
    const min = side === 'left' || side === 'right' ? this.patientInfoMinWidth : this.patientInfoMinHeight;
    const safeMin = Math.min(min, max);
    return this.clamp(size || safeMin, safeMin, max);
  }

  private getPatientInfoAvailableArea(): { top: number, bottom: number, width: number, height: number } {
    const viewport = this.getPatientInfoViewport();
    const top = this.getPatientInfoTopMenuOffset(viewport.height);
    return {
      top,
      bottom: viewport.height,
      width: viewport.width,
      height: Math.max(0, viewport.height - top)
    };
  }

  private getPatientInfoTopMenuOffset(viewportHeight: number): number {
    const topBar = document.querySelector('div.top-bar') as HTMLElement;
    const topMenu = document.querySelector('div.top-menu-area') as HTMLElement;
    const pageTitle = document.querySelector('app-main-layout .page-title:not([hidden])') as HTMLElement;
    const bottom = [topBar, topMenu, pageTitle]
      .filter(element => !!element)
      .map(element => element.getBoundingClientRect().bottom)
      .reduce((max, value) => Math.max(max, value), 0);
    return this.clamp(Math.ceil(bottom), 0, Math.max(0, viewportHeight - 1));
  }

  private getPatientInfoViewport(): { width: number, height: number } {
    return {
      width: window.innerWidth || document.documentElement.clientWidth || 1024,
      height: window.innerHeight || document.documentElement.clientHeight || 768
    };
  }

  private emitLayoutChange() {
    const layout: PatientInfoPinnedLayout | null = !this.patientInfoWindow.pinned || !this.patientInfoWindow.pinSide
      ? null
      : {
        side: this.patientInfoWindow.pinSide,
        size: Math.round(this.patientInfoWindow.minimized
          ? this.patientInfoPinnedMinimizedThickness
          : this.getClampedPatientInfoPinnedSize(this.patientInfoWindow.pinSide, this.patientInfoWindow.pinnedSize))
      };

    this.layoutChange.emit(layout);
    this.patientInfoWindowService.emitLayoutChange(layout);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  formatRocBirthday(value: any): string {
    const date = DateHelper.createDate(value);
    return date ? DateHelper.getROCFullDateString(date, '/') : '';
  }
}
